import { CommonModule } from "@angular/common";
import { Overlay, ScrollStrategy } from "@angular/cdk/overlay";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { AngularSvgIconModule } from "angular-svg-icon";
import { forkJoin, Observable, Subscription } from "rxjs";
import { shareReplay, tap, timeout } from "rxjs/operators";
import { BaseModalComponent } from "../base-modal/base-modal.component";
import {
  NovaCompanionChatService,
  ReplyChunk,
  TurnTimings,
} from "../../services/nova-companion-chat.service";
import {
  BufferBand,
  ChatHistoryGroup,
  ChatHistoryTurn,
  ChatMessage,
  NOVA_NAV_LABELS,
  NOVA_NAV_ROUTES,
  NovaNavTarget,
  NovaResolvedNav,
  novaNavTargetOfResolved,
  novaResolvedNavRoute,
  ParsedNav,
  ParsedSafetyEscalation,
} from "../../services/nova-companion-chat.model";
import { NovaRealtimeToolCallService } from "../../services/nova-realtime-tool-call.service";
import { NovaWalkthroughService } from "../../services/nova-walkthrough.service";
import { N02HowNovaWorksService } from "../../services/onboarding-lessons/n02-how-nova-works.service";
import { N03ADictateMessageService } from "../../services/onboarding-lessons/n03a-dictate-message.service";
import { N03BTalkWithNovaService } from "../../services/onboarding-lessons/n03b-talk-with-nova.service";
import { N04SafetyBufferHeaderService } from "../../services/onboarding-lessons/n04-safety-buffer-header.service";
import { FlipCoachTargetDirective } from "../coach-mark/coach-mark-target.directive";
import { NovaFirstUsePanelComponent } from "../nova-first-use-panel/nova-first-use-panel.component";
import {
  NovaRealtimeStartResult,
  NovaRealtimeToolCallOutcome,
  NovaRelayedTurn,
} from "../../services/nova-realtime.model";
import {
  NOVA_VOICE_METER_BARS,
  NovaCompanionLauncherStateService,
} from "../../services/nova-companion-launcher-state.service";
import {
  describeMissingVoiceSupport,
  probeLegacyVoiceSupport,
  probeRealtimeVoiceSupport,
} from "./nova-voice-support";
import { KeyboardViewportWindow, watchKeyboardViewport } from "./nova-keyboard-viewport";
import {
  NovaRealtimeVoiceUiService,
  scrubCaption,
} from "../../services/nova-realtime-voice-ui.service";
import { NovaRealtimeVoiceView } from "../../services/nova-realtime-voice-ui.model";
import { NovaRealtimeSessionService } from "../../services/nova-realtime-session.service";
import { NovaRealtimeRelayService } from "../../services/nova-realtime-relay.service";

interface ThreadItem {
  kind: "message" | "nav" | "typing" | "divider";
  role?: "user" | "assistant";
  text?: string;
  /** Only on kind === "nav": the [[NAV:X]] target this toast routes to. Held per item, not
   *  on the component, so an older toast still routes where it says it does. */
  navTarget?: NovaNavTarget;
  /** Only on kind === "nav", and only where the server resolved a specific lesson or chapter.
   *  Held per item for the same reason navTarget is: the toast must keep routing where its label
   *  says, however many turns later it is clicked. */
  navResolved?: NovaResolvedNav;
}

/**
 * Reads a timestamp from the chat API, whose times are documented as UTC.
 *
 * ECMAScript parses a date-time carrying no zone as LOCAL time, so a bare "2026-08-17T04:30:00"
 * lands ten hours early in Australia. That is not hypothetical: SQL Server stores no offset, so
 * these arrive without a Z unless the server stamps one on. Ten hours is enough to date a
 * conversation from a minute ago as yesterday - which silently ended the conversation the user was
 * still in, and put the wrong heading above every divider.
 *
 * Applying the documented contract rather than trusting the format is the point: the server now
 * sends the Z, and this stays correct whether or not it does.
 */
function parseApiTimestamp(value: string): Date {
  if (!value) return new Date(NaN);
  const carriesZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  return new Date(carriesZone ? value : `${value}Z`);
}

/**
 * Splits a flat list of stored turns into the conversations a reader should see, oldest first.
 *
 * The server decides WHICH conversation each turn belongs to; this decides how that reads. Turns
 * predating conversations have no id, and are grouped by calendar day instead - the honest
 * approximation, and better than running weeks of unrelated exchanges together under one heading.
 */
function groupHistoryIntoConversations(turns: ChatHistoryTurn[]): ChatHistoryGroup[] {
  const groups: ChatHistoryGroup[] = [];

  for (const turn of turns) {
    const occurred = parseApiTimestamp(turn.occurredAt);

    // Legacy turns fall back to a per-day key. Prefixed so a day key can never collide with a
    // real conversation id.
    const key = turn.conversationId ?? `day:${occurred.toDateString()}`;
    const last = groups.length > 0 ? groups[groups.length - 1] : null;

    if (last && last.key === key) {
      last.turns.push(turn);
      continue;
    }

    groups.push({ key, heading: describeWhen(occurred), turns: [turn] });
  }

  return groups;
}

/**
 * Separates the conversation that is still in progress from the ones that are over.
 *
 * Mirrors the rule the server applies in `ResolveConversationIdAsync`: a new turn joins the latest
 * conversation while that conversation's last turn is newer than the idle threshold. The sheet is
 * destroyed on close, so without this a reopen a few seconds later renders the exchange the user is
 * still having as finished history - and, worse, posts an empty conversation on the next send. The
 * server excludes the live conversation from the context it renders into the system prompt
 * precisely because the client is expected to be posting it, so nothing else would carry it.
 *
 * A turn with no `conversationId` predates conversations. The server would give it a fresh id
 * rather than adopt it, so it is never resumable here either - treating it as resumable would
 * invent a continuation the server will not honour.
 */
function splitCurrentConversation(
  groups: ChatHistoryGroup[],
  idleMinutes: number,
  now: number
): { previous: ChatHistoryGroup[]; current: ChatHistoryGroup | null } {
  const last = groups.length > 0 ? groups[groups.length - 1] : null;
  if (!last || last.turns.length === 0) {
    return { previous: groups, current: null };
  }

  const lastTurn = last.turns[last.turns.length - 1];
  if (!lastTurn.conversationId) {
    return { previous: groups, current: null };
  }

  const idleMs = idleMinutes * 60 * 1000;
  if (now - parseApiTimestamp(lastTurn.occurredAt).getTime() >= idleMs) {
    return { previous: groups, current: null };
  }

  return { previous: groups.slice(0, groups.length - 1), current: last };
}

/**
 * The heading above a past conversation. Relative for the recent past, because "Yesterday" places
 * a conversation for a reader in a way "2026-08-16" does not; absolute once relative stops being
 * meaningful.
 */
function describeWhen(when: Date): string {
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(new Date()) - startOfDay(when)) / 86400000);

  if (days <= 0) return "Earlier today";
  if (days === 1) return "Yesterday";

  // Inside a week a weekday name is enough to locate it. Beyond that "Tuesday" is ambiguous
  // between two Tuesdays, so switch to a date.
  if (days < 7) {
    return when.toLocaleDateString(undefined, { weekday: "long" });
  }

  return when.toLocaleDateString(undefined, { day: "numeric", month: "long" });
}

/** One bar of the dictation waveform: its resting height, as a fraction of the wave's, and its phase. */
interface WaveBar {
  heightPercent: number;
  delayMs: number;
}

const padTwo = (value: number): string => (value < 10 ? "0" : "") + value;

/** mm:ss, both zero-padded - the design handoff shows "00:08", not "0:08". */
function formatDictateTime(totalSeconds: number): string {
  return `${padTwo(Math.floor(totalSeconds / 60))}:${padTwo(totalSeconds % 60)}`;
}

/**
 * FLIP-173's "investigate and tune timeout sensitivity" requirement. The browser's own
 * end-of-speech heuristic cuts the recognizer off at natural pauses no matter what
 * `continuous` is set to - that part genuinely isn't controllable from application code,
 * per the ticket's own note. So rather than adding our own (shorter) timeout on top, we
 * restart the recognizer whenever the browser ends it and the user hasn't pressed stop:
 * the stop button is the only thing that ends dictation.
 *
 * A restart cycle that ends faster than this without producing a single result means the
 * recognizer can't actually run - no microphone, or permission revoked mid-session - so
 * bail out after a few rather than spinning start/end forever.
 */
const DICTATE_MIN_HEALTHY_SESSION_MS = 250;
const DICTATE_MAX_FAILED_RESTARTS = 3;

/** Recognition errors a restart can't recover from - the mic simply isn't available to us. */
const DICTATE_TERMINAL_ERRORS = ["not-allowed", "service-not-allowed", "audio-capture"];

/**
 * Bars in the dictation waveform. The bars have a fixed 5px pitch (2px wide, 3px gap) to
 * match the design's density, and the row overflows hidden - so this only has to be
 * *enough* to fill the pill at the widest the modal ever gets (--modal-width tops out at
 * 480px for size="standard"), and narrower widths simply clip the tail. Distributing a
 * fixed count across the width instead would leave the wave sparse at 480px or crowded at
 * the 280px floor.
 */
const DICTATE_WAVE_BAR_COUNT = 76;

const FALLBACK_CHIPS = [
  "How's my Buffer looking this week?",
  "I've been finding things tough lately.",
  "Help me think about tomorrow.",
  "What have my Stars been this week?",
];

/** Single-line height in px - kept in sync with .nova-companion-chat__input's CSS. */
const SINGLE_LINE_HEIGHT = 46;

/** Auto-grow ceiling in px - kept in sync with .nova-companion-chat__input's max-height. */
const MAX_INPUT_HEIGHT = 120;

/** Total vertical border on the input - kept in sync with its 1px top/bottom border. */
const INPUT_BORDER_Y = 2;

/**
 * How long a smooth scroll may still be animating after we start one. While it
 * runs, isNearBottom() reports an intermediate position, so treat "we scrolled
 * very recently" as "the user is following the thread" rather than re-sampling.
 */
const SMOOTH_SCROLL_SETTLE_MS = 500;

// ── Voice mode (FLIP-158) ──

/**
 * Where voice mode is in the turn-taking loop.
 *
 * Half-duplex on the RECOGNISER, which is stopped while Nova speaks so she never transcribes her
 * own voice - but not on the microphone, which stays open for the whole session so the level
 * monitor can hear someone talking over her.
 *
 * "interrupted" is the state that makes this feel like a conversation rather than a walkie-talkie:
 * the user has started talking, Nova's audio is PAUSED rather than destroyed, and what happens
 * next depends on what they actually say. A backchannel ("mm-hm") resumes her from where she
 * stopped; anything substantive ends her turn and becomes the next message. Until that is decided,
 * her queue, chunk plans and speechTurn are all still intact - which is the whole difference
 * between pausing and aborting.
 */
type VoiceState = "off" | "listening" | "thinking" | "speaking" | "interrupted";

/**
 * Sentence chunking bounds for speech synthesis.
 *
 * OpenAI's /v1/audio/speech does not stream - it returns the complete audio or nothing - so
 * synthesising a whole reply in one call means the user hears silence for as long as that call
 * takes. Splitting the reply and pipelining the calls lets playback start on the first sentence.
 *
 * MIN exists because the split is only worth making when the chunk is big enough to cover the
 * next chunk's synthesis while it plays; below that the per-request overhead dominates and short
 * fragments get merged into their neighbour instead. MAX caps how long the FIRST chunk can make
 * the user wait, which is the whole point of chunking.
 */
const VOICE_CHUNK_MIN_CHARS = 80;
const VOICE_CHUNK_MAX_CHARS = 300;

/**
 * The same floor, for the FIRST chunk of a reply only.
 *
 * MIN is sized so a chunk plays for long enough to cover the NEXT one being synthesised. Nothing
 * is playing yet when the first chunk is cut, so that argument does not apply to it - and the
 * thing it trades against, the wait before Nova makes any sound at all, is at its most expensive
 * there. It costs twice over: the reply has to stream 80 characters before the chunk exists, and
 * then those 80 characters have to be synthesised before it plays.
 *
 * The cost of getting this wrong is a pause after her first sentence, where the second chunk is
 * still being synthesised. That is a pause between sentences, which is a thing people do anyway,
 * and it buys the user hearing her start sooner - which is the delay they actually notice.
 */
const VOICE_FIRST_CHUNK_MIN_CHARS = 30;

/** The floor that applies to the chunk at a given position. See VOICE_FIRST_CHUNK_MIN_CHARS. */
function chunkMinChars(index: number): number {
  return index === 0 ? VOICE_FIRST_CHUNK_MIN_CHARS : VOICE_CHUNK_MIN_CHARS;
}

/**
 * Splits a reply into speakable chunks on sentence boundaries.
 *
 * Merges fragments below VOICE_CHUNK_MIN_CHARS forward into the next sentence, so "Hi." doesn't
 * become its own round-trip. A single sentence longer than VOICE_CHUNK_MAX_CHARS is split on
 * whitespace rather than mid-word - an over-long sentence is rare, but cutting one through the
 * middle of a word is audible and cutting it at a space is not.
 */
/**
 * How many chunks of a still-arriving reply are safe to speak.
 *
 * The last chunk is normally held back, because the splitter merges anything under its floor
 * forward into the next sentence - so a chunk that is still the last one might yet grow, and
 * speaking it would say the same words twice.
 *
 * That is only true while it CAN still grow. A chunk that has reached its floor and ends on
 * terminal punctuation was cut by the length rule, and re-splitting a longer version of the same
 * reply makes that identical cut in the identical place: nothing arriving later can reach back
 * past a boundary the splitter has already committed to. Holding it back anyway costs an entire
 * extra sentence of streaming before Nova says a word, which was the single largest avoidable
 * delay in the turn.
 */
export function speakableChunkCount(chunks: string[]): number {
  if (chunks.length === 0) return 0;
  const last = (chunks[chunks.length - 1] ?? "").trim();
  const settled =
    /[.!?]["'\)\]]*$/.test(last) && last.length >= chunkMinChars(chunks.length - 1);
  return settled ? chunks.length : chunks.length - 1;
}

export function splitIntoSpeechChunks(text: string): string[] {
  const trimmed = (text ?? "").trim();
  if (!trimmed) return [];

  // Keep the terminator with its sentence - it's what gives the synthesiser its intonation.
  const sentences = trimmed.match(/[^.!?]+[.!?]*\s*/g) ?? [trimmed];

  const chunks: string[] = [];
  let pending = "";

  const flushLongChunk = (chunk: string): void => {
    let rest = chunk;
    while (rest.length > VOICE_CHUNK_MAX_CHARS) {
      const window = rest.slice(0, VOICE_CHUNK_MAX_CHARS);
      const breakAt = window.lastIndexOf(" ");
      // No space at all in a full window: a single unbroken token, so a hard cut is the
      // only option left.
      const cut = breakAt > 0 ? breakAt : VOICE_CHUNK_MAX_CHARS;
      chunks.push(rest.slice(0, cut).trim());
      rest = rest.slice(cut).trim();
    }
    if (rest) chunks.push(rest);
  };

  for (const sentence of sentences) {
    pending += sentence;
    // The first chunk gets a lower floor - see VOICE_FIRST_CHUNK_MIN_CHARS. Keyed off how many
    // chunks exist rather than off a flag, so re-splitting a growing reply makes the same cut in
    // the same place every time.
    if (pending.trim().length >= chunkMinChars(chunks.length)) {
      flushLongChunk(pending.trim());
      pending = "";
    }
  }
  // Trailing fragment is below MIN, so it has no next sentence to merge into - it ships as-is
  // rather than being dropped.
  if (pending.trim()) flushLongChunk(pending.trim());

  return chunks.filter((chunk) => chunk.length > 0);
}

/**
 * How long the user has to stop talking before voice mode treats the turn as finished.
 *
 * The browser fires its own `onend` eventually, but only on Chrome's end-of-speech heuristic,
 * which waits several seconds - and longer with `continuous = true`, which dictation needs so the
 * recognizer survives mid-sentence pauses. That wait was dead air before the request even left
 * the browser. Timing our own silence instead hands the turn over as soon as the user is
 * plausibly done.
 *
 * 900 -> 1500 -> 800 -> 600. Each reduction has been paid for by strengthening the evidence
 * rather than by accepting more risk, and this one is no exception: the acoustic veto now runs on
 * VOICE_SPEECH_HOLD_MS rather than on the waveform's 600ms drawing hold, so the microphone answers
 * "have they stopped" 350ms sooner than it used to. The window is what runs BEFORE the veto is
 * consulted, and a veto that responds faster is what lets it be shorter. The mid-thought window
 * below is deliberately not reduced with it - being cut off mid-sentence is the failure that
 * matters, and that is the case it exists for.
 *
 * It went 900 -> 1500 to stop people being cut off, and has come back down now the veto exists:
 * waiting longer was standing in for evidence, and the veto IS the evidence. Every millisecond
 * here is dead air between someone finishing and Nova starting, so this wants to be as small as
 * the evidence allows rather than as large as it can get away with. What the timer measures is
 * time since the TRANSCRIPT last grew, and the recognizer stops emitting the instant someone
 * pauses to think - which is not the same thing as being finished.
 */
const VOICE_END_OF_UTTERANCE_MS = 600;

/**
 * The window when the recognizer has put a full stop, question mark or exclamation on the end.
 *
 * Chrome punctuates its FINAL results, and only where it believes a sentence closed. That is a
 * much stronger signal of "finished" than silence is, and it earns a shorter wait.
 */
const VOICE_END_OF_UTTERANCE_SETTLED_MS = 400;

/**
 * The same window, for an utterance that has stopped on a word nobody stops a sentence on.
 *
 * "I've been feeling kind of..." is a pause with more coming; "I slept badly" is a turn being
 * handed over. Both are silence, and the acoustic veto can't separate them, so the last word
 * does - see VOICE_CONTINUATION_WORDS.
 */
const VOICE_END_OF_UTTERANCE_TRAILING_MS = 2000;

/**
 * FLIP-232. How early to start the recognizer before Nova's last chunk finishes playing.
 *
 * SpeechRecognition does not capture the instant start() returns - the browser has a mic stream
 * and a service connection to bring up first - so a recognizer started at her onended is deaf for
 * its own first few hundred milliseconds. A sentence survives that, because its tail lands after
 * the recognizer is live; "okay" does not, and has to be said twice. That is the whole bug.
 *
 * So the restart moves EARLIER than the handover rather than the handover moving later: the
 * recognizer is already warm when the floor actually passes. What it hears in the meantime is
 * Nova, so everything from before the handoff instant is discarded - see adoptWarmRecognizer.
 *
 * A guess at Chrome's startup latency, deliberately one named constant so it can be tuned from
 * what staging actually does. Too small and the window reopens; too large and there is more of
 * her own tail to throw away, which costs nothing but is not free of risk (see stripWarmupPrefix).
 */
const VOICE_RECOGNIZER_WARMUP_MS = 400;

/**
 * How often to re-check once the timer has fired but the microphone still hears the user.
 *
 * Short, because this is the last thing standing between "still talking" and Nova taking the
 * turn - polling more slowly would add its own lag to the handover.
 */
const VOICE_END_RECHECK_MS = 150;

/**
 * The longest the microphone's veto can hold a turn open past the last word transcribed.
 *
 * Without it a fan, a television or a busy cafe would keep the veto permanently on and the turn
 * would never end. This is the point where "still talking" stops being a credible explanation
 * for a transcript that has not grown.
 */
const VOICE_END_MAX_WAIT_MS = 6000;

/**
 * Words that don't end a thought. Anything trailing off on one of these buys the longer window.
 *
 * Function words and fillers only - the test is "could this be the last word of a finished
 * sentence", not "is this word common". Matched against the recogniser's own lowercase output,
 * which carries no punctuation to work from mid-utterance.
 */
const VOICE_CONTINUATION_WORDS = new Set([
  "a", "about", "actually", "am", "an", "and", "are", "as", "at", "because", "been", "but", "by",
  "cause", "do", "er", "for", "from", "get", "getting", "had", "has", "have", "her", "him", "his",
  "how", "i", "if", "in", "into", "is", "it", "its", "just", "kind", "like", "little", "me", "mm",
  "more", "my", "not", "of", "on", "or", "our", "really", "so", "some", "sort", "than", "that",
  "the", "their", "them", "then", "they", "this", "to", "uh", "um", "very", "was", "we", "well",
  "were", "what", "when", "which", "while", "who", "why", "with", "would", "you", "your",
]);

/**
 * Whether a transcript looks like it stopped mid-thought rather than at the end of one.
 *
 * Deliberately one word deep. A parser would be no more accurate against interim results that
 * arrive without punctuation, and would be wrong in ways that are much harder to explain when
 * someone is cut off anyway.
 */
function endsMidThought(text: string): boolean {
  const words = text.toLowerCase().match(/[a-z']+/g);
  const last = words?.[words.length - 1];
  return last !== undefined && VOICE_CONTINUATION_WORDS.has(last);
}

/** Bars in the listening bar's live waveform. Matches the dictate overlay's visual language. */
/**
 * Bar geometry, in px. MUST match the `span` width and the row `gap` in the SCSS - the count of
 * bars is derived from the strip's measured width, so the two have to agree or the wave either
 * overflows its box or leaves a gap before the mic.
 */
const VOICE_WAVE_BAR_WIDTH = 2;
const VOICE_WAVE_BAR_GAP = 3;

/**
 * Bounds on the derived count. The floor keeps the wave legible if the strip is measured mid-
 * animation at a few px; the ceiling is a backstop against a runaway measurement, not a design
 * limit - the widest the modal gets is ~480px, which comes to about 65 bars.
 */
const VOICE_WAVE_MIN_BAR_COUNT = 8;
const VOICE_WAVE_MAX_BAR_COUNT = 96;

/**
 * Floor for a bar while the wave is on screen, as a percentage. Only applies mid-utterance - a
 * quiet syllable shrinks to a dot rather than vanishing, so the wave stays a wave.
 */
const VOICE_WAVE_MIN_HEIGHT = 12;

/**
 * Squash the sheet's wave down to the handful of bars the minimised launcher's circle can show.
 *
 * Buckets and takes the loudest in each, rather than sampling every Nth bar. The levels are
 * log-spaced across 80Hz-6kHz (see mapVoiceWaveBins), so sampling would silently discard whole
 * octaves - a voice sitting in a dropped band would move the sheet's meter and leave the circle
 * flat. Taking the max keeps every frequency represented in whichever bar it falls under, and
 * matches how the eye reads the full-width version anyway: as an envelope, not as 40 readings.
 */
function compactVoiceLevels(levels: number[], barCount: number): number[] {
  if (levels.length === 0) return new Array(barCount).fill(VOICE_WAVE_MIN_HEIGHT);
  if (levels.length <= barCount) {
    // Fewer bars to give than asked for - pad rather than stretch, so the wave keeps its shape.
    return Array.from(
      { length: barCount },
      (_, bar) => levels[bar] ?? VOICE_WAVE_MIN_HEIGHT
    );
  }

  return Array.from({ length: barCount }, (_, bar) => {
    const start = Math.floor((bar * levels.length) / barCount);
    const end = Math.max(start + 1, Math.floor(((bar + 1) * levels.length) / barCount));
    let peak = VOICE_WAVE_MIN_HEIGHT;
    for (let i = start; i < end; i++) {
      const level = levels[i] ?? VOICE_WAVE_MIN_HEIGHT;
      if (level > peak) peak = level;
    }
    return peak;
  });
}

/**
 * Amplitude (0-1 RMS) the audio has to exceed for the wave to appear at all.
 *
 * The wave is hidden below this rather than idling at the floor: an always-on wave next to "Nova
 * is listening" reads as Nova hearing something, so showing it before anyone has said anything is
 * a claim the meter cannot support.
 *
 * Measured on the TIME-DOMAIN signal, which is a linear amplitude with a real zero. The frequency
 * bytes that drive the bar heights are decibels mapped across the analyser's minDecibels..
 * maxDecibels window (-100..-30 by default), so the same-looking 0-1 number over there means
 * something else entirely: 0.08 of that range is -94dBFS, below the floor of an open mic in an
 * empty room, which is why the previous threshold let room tone raise the wave.
 *
 * Digital silence reads 0.0, a suppressed room floor under 0.01, ordinary speech 0.05-0.2. This
 * sits in the gap rather than under both.
 */
const VOICE_WAVE_SPEECH_RMS = 0.03;

/**
 * How long the wave stays up after the level drops back under the threshold.
 *
 * Normal speech dips below it constantly - between words, and through the closure of any stop
 * consonant. Without a hold the wave would strobe on and off several times per sentence. Nova's
 * own speech has the same gaps, plus the seam between two synthesised chunks.
 */
const VOICE_WAVE_HOLD_MS = 600;

/**
 * How long after the last sound the microphone still counts as hearing someone TALK.
 *
 * Separate from the hold above, which is a display constant: 600ms exists so the bars do not
 * strobe on and off through every stop consonant, and nothing about that number was ever chosen
 * to answer "has this person finished". Using it for both meant the acoustic veto kept a turn open
 * for 600ms after the room had actually gone quiet - dead air, on every single turn, inherited
 * from a decision about drawing.
 *
 * Shorter, because the trailing edge is all this is used for. Where the veto genuinely earns its
 * keep - someone still talking while the transcript lags behind - the microphone is loud NOW and
 * the hold length does not enter into it.
 */
const VOICE_SPEECH_HOLD_MS = 250;

/**
 * How loud the microphone has to hear the user before it will cut Nova off, as RMS.
 *
 * MEASURED, not guessed - see docs/nova-mic-echo-test.html, which produced these numbers on a
 * real machine with the app's own getUserMedia settings:
 *
 *   echo floor (Nova through the speakers, user silent)  0.0089
 *   the user's voice while she is speaking               0.2005
 *
 * So echo cancellation is doing its job here: there is a factor of twenty between the two, and
 * the threshold wants to sit low in that gap rather than close to the voice. 0.04 is four times
 * the echo floor and five times under a normal speaking level, which leaves room for someone
 * interrupting quietly - which is how people interrupt, since they are not expecting to have to
 * shout over her.
 *
 * If Nova starts cutting herself off, this is the number to raise, and the page above is how to
 * find the right one on a different machine.
 */
const VOICE_BARGE_IN_RMS = 0.04;

/**
 * Runtime overrides, read from localStorage so this can be tuned against a real microphone in a
 * page refresh rather than a deploy.
 *
 *   localStorage.novaBargeInFloor = "0.02"      // the floor under the adaptive gate
 *   localStorage.novaBargeInEchoRatio = "0.15"  // how far over Nova's own level counts
 *   localStorage.novaVoiceDebug = "1"           // log the level and the decision, 4x a second
 *
 * They exist because the threshold cannot be reasoned to a correct value: how much of Nova's own
 * voice survives echo cancellation, and how much of the user's survives the suppression that
 * comes with it, differ per machine, per microphone and per whether anyone is wearing headphones.
 */
function voiceDebugSetting(key: string): string | null {
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    // Storage disabled, private mode, or a sandboxed frame. Diagnostics are never load-bearing.
    return null;
  }
}

/**
 * The absolute floor, overridable at runtime.
 *
 * Deliberately a DIFFERENT key from the old novaBargeInRms. That one set the whole threshold; this
 * sets a floor underneath an adaptive gate, which is not the same quantity, and a value tuned for
 * the old meaning is actively wrong for the new one. A stale novaBargeInRms of 0.086 was doing
 * exactly that - sitting inside the range a real speaking voice occupies, so genuine speech was
 * rejected and barge-in could not fire however hard anyone talked. Renaming retires those values
 * rather than silently reinterpreting them.
 */
function bargeInThreshold(): number {
  const override = Number(voiceDebugSetting("novaBargeInFloor"));
  return Number.isFinite(override) && override > 0 ? override : VOICE_BARGE_IN_RMS;
}

/** localStorage.novaBargeInEchoRatio - see VOICE_BARGE_IN_ECHO_RATIO. */
function bargeInEchoRatio(): number {
  const override = Number(voiceDebugSetting("novaBargeInEchoRatio"));
  return Number.isFinite(override) && override > 0 ? override : VOICE_BARGE_IN_ECHO_RATIO;
}

/**
 * How long that has to hold before the turn changes hands.
 *
 * A cough, a door, or one syllable of leaked echo should not stop her. Someone actually talking
 * over her clears this comfortably.
 */
const VOICE_BARGE_IN_MS = 300;

/**
 * How long the level may sit BELOW the gate without the interruption being written off.
 *
 * Speech is not continuous, and requiring it to be is what stopped barge-in working even where the
 * threshold was right. A real interruption reads 0.0940, 0.0054, 0.0923 from one frame to the
 * next: the dips are the gaps between words and the closure of every stop consonant, not the
 * speaker stopping. Resetting the accumulator on the first of them meant VOICE_BARGE_IN_MS could
 * never be reached - the counter ran 50, 102, 0, 50, 0 for the whole of someone talking over her.
 *
 * This is the problem VOICE_WAVE_HOLD_MS already solves for the waveform, and it is set shorter
 * here because a gap inside a word is far shorter than the gap between turns, and this only has to
 * survive the former.
 */
const VOICE_BARGE_IN_HANGOVER_MS = 250;

/**
 * How long Nova gets before she can be interrupted at all, measured from the moment the FLOOR
 * PASSED TO HER - the end of the user's turn - and not from her first spoken word.
 *
 * What this guards against is the tail of the user's own last sentence: a trailing word, a breath,
 * the "hmm" people put after a question, still in the room when the turn changes hands. That is a
 * property of when THEY stopped, so that is what it is measured from.
 *
 * Anchoring it to her first sound instead - which is what it did briefly - has a consequence that
 * looks harmless and is not. Her first sound is one /api/Nova/Speak round-trip after the turn
 * changed hands, so the window it protects has moved a second or more away from the tail it exists
 * to absorb, and the silence in between became uninterruptible for no reason at all. Where
 * synthesis is slow this grace has simply expired by the time she speaks, which is correct: by
 * then the user's tail is long gone and nothing needs protecting.
 *
 * 900 -> 500 on the measurement above: with the echo floor twenty times under a speaking voice,
 * the risk this guards against is small, and every millisecond of it is time where talking over
 * her does nothing and the user concludes it does not work.
 */
const VOICE_BARGE_IN_GRACE_MS = 500;

/**
 * How far above Nova's OWN playback level the microphone has to sit before it counts as someone
 * talking over her.
 *
 * The absolute threshold above cannot be made correct, and two commits of tuning it are the
 * evidence. What survives echo cancellation is proportional to what the speakers are doing, so a
 * fixed number is being asked to sit above the echo of Nova at full volume and below a user
 * interrupting quietly - a window that moves with speaker volume, mic gain, and whether anyone is
 * wearing headphones. On some machines it does not exist at all.
 *
 * Both analysers are already live in the same frame, so the leak can be measured instead of
 * guessed: the gate is whichever is HIGHER of the absolute floor and this fraction of Nova's
 * current output.
 *
 * MEASURED from the monitor's own debug output on Jen's machine: mic 0.0058 while Nova plays at
 * 0.2161 - about 2.7% - and with Nova silent the mic still reads 0.0050. The echo is at or below
 * the microphone's own noise floor, which is to say echo cancellation there is working completely.
 * 0.15 is five times the measured leak.
 *
 * The first value here was 0.35, sized for a leak an order of magnitude worse, and it did real
 * harm rather than none: with Nova at 0.2161 it lifted the gate to 0.076, which is INSIDE the
 * range a real speaking voice occupies on that machine (0.09-0.18 at the peaks, and lower between
 * words). It was rejecting Jen's own speech as echo. Where echo cancellation works, this term
 * should sit below the floor and never bind - and there it now does.
 *
 * The property that matters most is what happens BETWEEN her words. Her playback drops to near
 * zero in every gap, so the gate falls back to the absolute floor exactly then - which is when
 * people actually start talking, and when the echo they would be measured against is not there.
 */
const VOICE_BARGE_IN_ECHO_RATIO = 0.15;

/**
 * How long Nova stays paused with nothing being said before she simply carries on.
 *
 * Covers the false positive the level gate cannot avoid - a cough, a chair, a door - where she
 * stops, hears no words at all, and should pick her sentence back up rather than sit in silence
 * waiting for a turn the user never meant to take.
 */
const VOICE_INTERRUPT_RESUME_MS = 1200;

/**
 * How often to re-ask, once the resume window has passed but the microphone still hears someone.
 */
const VOICE_INTERRUPT_RECHECK_MS = 200;

/**
 * The longest Nova will stay paused waiting for words that never arrive.
 *
 * The microphone alone would hold her paused forever in a noisy room, exactly as it would hold the
 * user's turn open forever in onVoiceEndTimer - and for the same reason, it is capped. Longer than
 * that cap, because someone interrupting to ADD CONTEXT is mid-explanation, not mid-answer.
 */
const VOICE_INTERRUPT_MAX_WAIT_MS = 8000;

/**
 * Sounds that mean "I am listening", not "it is my turn".
 *
 * People say these THROUGH someone else's sentence without any intention of taking the floor, and
 * treating them as a turn is what makes a voice assistant feel like it is fighting you.
 *
 * Matched as whole PHRASES rather than as a bag of words, which is the bug this replaced: a set
 * containing "i", "see", "got" and "it" - there to spell "i see" and "got it" - also matched a
 * bare "I". The recogniser is cold-started by the pause itself, so a bare "I" is exactly what the
 * FIRST fragment of "I was thinking..." looks like, and Nova resumed over the top of someone who
 * had just started explaining something. Every entry here is an utterance that is complete on its
 * own; a fragment of a longer sentence is not one.
 *
 * "yes" and "no" are deliberately absent. Nova asks questions, and those are answers to them.
 */
const VOICE_BACKCHANNEL_PHRASES = new Set([
  "mm",
  "mmm",
  "mm hm",
  "mm hmm",
  "mhm",
  "mhmm",
  "hm",
  "hmm",
  "uh huh",
  "uhuh",
  "ah",
  "aha",
  "oh",
  "ooh",
  "ok",
  "okay",
  "yeah",
  "yep",
  "yup",
  "right",
  "sure",
  "true",
  "exactly",
  "totally",
  "wow",
  "really",
  "i see",
  "got it",
  "gotcha",
  "makes sense",
  "of course",
  "fair enough",
  "good point",
  "cool",
  "nice",
]);

/**
 * Normalises an utterance for phrase matching: lowercase, and every run of non-letters becomes a
 * single space. Punctuation and hyphens both fall out, so "Mm-hm.", "mm hm" and "MMHM" - which the
 * recogniser produces interchangeably - all reduce to the same key.
 */
function normaliseUtterance(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z]+/g, " ")
    .trim();
}

/**
 * Whether an utterance is an acknowledgement rather than a bid for the turn.
 *
 * Empty is deliberately NOT a backchannel: "she stopped and no words arrived" is the resume
 * timer's job, and conflating the two would have a failed recognition resume her as though the
 * user had agreed with something.
 */
function isBackchannel(text: string): boolean {
  const normalised = normaliseUtterance(text);
  if (!normalised) return false;
  return VOICE_BACKCHANNEL_PHRASES.has(normalised);
}


/**
 * Analyser window, in samples.
 *
 * Two things size this. The RMS gate needs a window long enough to be stable - 64 samples is
 * 1.3ms, short enough that a zero crossing inside a vowel reads as silence, where 1024 is 21ms.
 * The bars need frequency resolution: at 1024 the bins are ~47Hz apart, so the sixty-odd bars a
 * full-width strip holds each land on a distinct one instead of repeating their neighbour.
 */
const VOICE_WAVE_FFT_SIZE = 1024;

/**
 * The frequency range the bars span, in Hz, from the bottom of a speaking voice to the top of its
 * fricatives. Outside it the bins are near-dead for speech and those bars would sit flat.
 *
 * Spanned LOGARITHMICALLY, which matters more the wider the strip gets. Spread linearly, the
 * bottom two octaves - where almost all of a voice's energy is - would land inside the first
 * couple of bars and leave the rest of a full-width wave permanently flat. Log spacing gives each
 * octave the same number of bars, so the whole strip moves when someone talks.
 */
const VOICE_WAVE_MIN_HZ = 80;
const VOICE_WAVE_MAX_HZ = 6000;

/**
 * Frame interval for the waveform, in ms (~20fps).
 *
 * The analyser is polled on requestAnimationFrame outside Angular, but every repaint costs a
 * change-detection pass. 20fps still reads as continuous motion for something this small while
 * costing a third of what 60fps would.
 */
const VOICE_WAVE_FRAME_MS = 50;

/**
 * Smoothing applied to each bar between frames: the fraction of the previous height retained.
 *
 * Raw analyser output is jittery enough to look like noise rather than speech. Easing toward the
 * new value keeps the wave legible as "someone is talking" without lagging behind the voice.
 */
const VOICE_WAVE_SMOOTHING = 0.6;

// ── Docked voice bar (FLIP-234) ──

/**
 * Panel class added to the dialog while the sheet is collapsed to the voice bar.
 *
 * On the PANEL rather than on this component's host because the three things docking has to
 * change - the backdrop, the page scroll block and the dialog surface - are all outside this
 * component's style encapsulation, in the CDK overlay. See the matching rules in
 * sass/cdk-theme.scss.
 */
const DOCKED_PANEL_CLASS = "nova-companion-chat-panel--docked";

/**
 * What novaCompanionChatDialogConfig() hands the component through MAT_DIALOG_DATA.
 */
export interface NovaCompanionChatDialogData {
  /**
   * The dialog's own scroll strategy, passed by reference so the component can switch it off
   * while docked. See the factory below.
   */
  scrollStrategy: ScrollStrategy;
}

/**
 * FLIP-234. The one config every caller opens this component with.
 *
 * It was three inline objects (FAB, header badge, /test harness) until the voice session had to
 * survive being minimized, at which point two of the settings below stopped being cosmetic:
 * getting either wrong silently ends the call, and a fourth call site copying an older object
 * would reintroduce that. Hence one factory rather than an exported const - the scroll strategy
 * is stateful and each dialog needs its own instance.
 *
 * - closeOnNavigation: false. MatDialog's default is true, which is exactly right for a modal
 *   and exactly wrong for a docked one: the whole point of the docked bar is that the user goes
 *   and does something else in the app while Nova talks them through it. With the default, the
 *   first route change destroys the component, and ngOnDestroy tears down the microphone, the
 *   AudioContext and the recognizer with it. Nothing else re-opens the dialog on navigation, and
 *   the one in-chat navigation path (onNavChipClicked) closes it explicitly first, so this only
 *   removes a close nobody was relying on.
 *
 * - scrollStrategy, passed BOTH to the overlay and through data. The default block strategy
 *   freezes the page behind the dialog, so a docked bar would leave the user unable to scroll
 *   the very page they minimized Nova to use. Handing the component the instance lets it
 *   disable() the block while docked and enable() it again on restore.
 *
 *   Overriding html.cdk-global-scrollblock in CSS instead would restore scrolling, and was
 *   rejected: BlockScrollStrategy captures the scroll position on enable() and calls
 *   window.scroll() back to it on disable(), so closing Nova after the user had scrolled
 *   somewhere else would yank them back to wherever the page was when they first opened her.
 *   Toggling the strategy avoids that, and re-enabling on restore re-captures the current
 *   position, which is correct on both edges.
 */
export function novaCompanionChatDialogConfig(
  overlay: Overlay
): MatDialogConfig<NovaCompanionChatDialogData> {
  const scrollStrategy = overlay.scrollStrategies.block();
  return {
    panelClass: ["base-modal-panel", "nova-companion-chat-panel"],
    backdropClass: "base-modal-backdrop",
    closeOnNavigation: false,
    scrollStrategy,
    data: { scrollStrategy },
  };
}

/**
 * The Nova Companion chat modal (FLIP-173) - ported from the confirmed design handoff
 * (`nova-companion/companion-chat/`). Independent module: has no dependency on the
 * Insight Card feature (FLIP-169/170/171/172) and renders the same whether or not
 * Insights is enabled.
 *
 * Opened via MatDialog by NovaCompanionFabComponent and NovaCompanionHeaderBadgeComponent,
 * both through novaCompanionChatDialogConfig() below rather than an inline config object -
 * see that function for why the config can no longer be duplicated per call site.
 *
 * Two behaviours deliberately diverge from the handoff's plain-JS source, both per
 * FLIP-173's confirmed requirements:
 *  - Dictation never auto-sends on recognition end (the handoff's `stopDictation(true)`
 *    path calling `sendBtn.click()` is not ported) - the transcript stays in the field
 *    for the user to review/edit/send.
 *  - The mic button, send button, and dictate overlay are mutually exclusive via
 *    *ngIf rather than three always-present absolutely-positioned siblings with no
 *    explicit z-index (the handoff's actual bug: the mic/send buttons render above
 *    the dictate overlay because they come later in DOM order). Structural exclusivity
 *    removes the overlap entirely rather than patching stacking order.
 */
@Component({
  selector: "app-nova-companion-chat",
  templateUrl: "./nova-companion-chat.component.html",
  styleUrls: ["./nova-companion-chat.component.scss"],
  standalone: true,
  imports: [CommonModule, FormsModule, AngularSvgIconModule, BaseModalComponent, NovaFirstUsePanelComponent, FlipCoachTargetDirective],
})
export class NovaCompanionChatComponent implements OnInit, OnDestroy {
  @ViewChild("inputBox") inputBoxRef?: ElementRef<HTMLTextAreaElement>;

  // FLIP-234. Focus lands here when the sheet expands again; it lives in the half of the UI that
  // is hidden while docked, so it cannot be focused from the collapsed side's click handler
  // without waiting a tick. See restoreVoice. The other end of that pair used to be the docked
  // pill's restore button and is now NovaCompanionFabComponent's circle, which focuses itself.
  @ViewChild("voiceMinimizeBtn") voiceMinimizeRef?: ElementRef<HTMLButtonElement>;

  /**
   * A setter rather than a plain query because the strip comes and goes with the turn: this fires
   * with the element when voice mode starts drawing and with undefined when it stops, which is
   * exactly when the observer wants attaching and detaching.
   */
  @ViewChild("voiceWave") set voiceWaveRef(ref: ElementRef<HTMLElement> | undefined) {
    this.voiceWaveResizeObserver?.disconnect();
    this.voiceWaveResizeObserver = null;
    const element = ref?.nativeElement;
    if (!element) return;

    if (typeof ResizeObserver === "undefined") {
      // No observer to deliver the first size, so measure it - out of band, because this setter
      // runs mid-change-detection and resizing an array the template is bound to inside that pass
      // is what ExpressionChangedAfterItHasBeenChecked is.
      setTimeout(() => this.setVoiceBarCount(element.clientWidth));
      return;
    }
    // observe() delivers the current size straight away, so this covers the first measurement as
    // well as later ones - and it arrives in its own task, clear of the pass that installed it.
    this.voiceWaveResizeObserver = new ResizeObserver((entries) => {
      this.setVoiceBarCount(entries[0]?.contentRect.width ?? 0);
    });
    this.voiceWaveResizeObserver.observe(element);
  }

  // static: true because openProactive() runs in ngOnInit and the chat service's
  // synchronous (of()) paths push the greeting straight away - a dynamic query
  // would still be undefined at that point.
  @ViewChild(BaseModalComponent, { static: true }) private baseModal?: BaseModalComponent;

  thread: ThreadItem[] = [];
  chips: string[] = [];
  chipsLoading = false;
  showChips = false;

  inputText = "";
  sending = false;
  isInputMultiline = false;

  isListening = false;
  dictateTimerLabel = formatDictateTime(0);

  /**
   * FLIP-158. "off" means the composer behaves exactly as it did before voice existed - every
   * other state replaces the composer with the status bar.
   *
   * An accessor pair over a backing field rather than a plain property, purely so that every
   * transition republishes the minimised launcher's meter. Publishing only from the wave loop
   * is not enough: "thinking" produces no audio at all, so the loop is skipping its repaints for
   * the whole of Nova's pause and the circle would sit there still claiming she is listening.
   * Intercepting the assignment catches all of the (many) places that move this state without
   * each of them having to remember to. Both accessors are public because TypeScript requires
   * them to agree - as the plain field they replace already was.
   */
  get voiceState(): VoiceState {
    return this.voiceStateValue;
  }

  set voiceState(value: VoiceState) {
    this.voiceStateValue = value;
    this.publishVoiceMeter();
  }

  private voiceStateValue: VoiceState = "off";

  /**
   * FLIP-234. Whether the sheet is collapsed, with the call still running behind it.
   *
   * Bound to the host so the component's own stylesheet can hide <app-base-modal> without a
   * wrapper element around it - the template's root IS the modal.
   *
   * Only ever true while voice is active: stopVoiceMode() undocks unconditionally, so the
   * minimised launcher can never be left claiming Nova is listening after the session has ended.
   */
  @HostBinding("class.nova-companion-chat--docked") voiceDocked = false;

  /** FLIP-389: N02's first-use panel is up in place of the chat area (the composer is hidden too). */
  @HostBinding("class.nova-companion-chat--first-use") get firstUseClass(): boolean {
    return this.showFirstUse;
  }

  /** The dialog's own disableClose, held while docking suspends it. See minimizeVoice. */
  private closeDisabledBeforeDock = false;

  /**
   * Live bar heights (percentages) for the listening bar's waveform, driven by actual microphone
   * amplitude rather than a fixed animation. A canned loop would be worse than nothing here: it
   * would claim the mic is hearing the user when it might not be. Flat at the floor until the
   * analyser is running.
   */
  voiceLevels: number[] = new Array(VOICE_WAVE_MIN_BAR_COUNT).fill(VOICE_WAVE_MIN_HEIGHT);

  /**
   * Whether there is sound to draw right now - the user's voice while Nova listens, Nova's own
   * while she speaks. Gates the wave: while this is false the status bar shows the label and mic
   * alone, because a wave with nothing behind it claims someone is talking when nobody is.
   */
  voiceHasSignal = false;

  /**
   * Whether the microphone is hearing someone talk RIGHT NOW - the acoustic veto, on the shorter
   * hold. Every turn-taking decision reads this; only the waveform reads voiceHasSignal.
   */
  private voiceHearsSpeech = false;

  /**
   * FLIP-276. The Realtime path's view of the call, or null when that path is not the one running.
   *
   * Null is the meaningful value here, and every Realtime branch in this component tests it: it is
   * what keeps the legacy acoustic engine's behaviour byte-for-byte unchanged while both paths
   * exist. FLIP-278 decides which one opens; FLIP-279 removes the other.
   */
  realtimeVoice: NovaRealtimeVoiceView | null = null;

  /** Subscriptions held for the length of one Realtime call. Separate from the component's own,
   * because the call starts and ends many times inside one component lifetime. */
  private realtimeSubscription: Subscription | null = null;

  /**
   * FLIP-283. Realtime item ids already rendered into the thread, so one spoken turn is shown once.
   *
   * Cleared when a session starts rather than when it ends: two calls in one sheet are separate
   * conversations to the model and carry their own item ids, and a set left populated would be
   * matched against ids it can no longer receive.
   */
  private renderedRealtimeItems = new Set<string>();

  /**
   * One analyser per stream, kept for the length of the call.
   *
   * The wave alternates between Nova's voice and the user's several times a turn, and rebuilding
   * an analyser and its graph on every handover would be both wasteful and audible - a
   * MediaStreamAudioSourceNode is not free to create.
   */
  private realtimeAnalysers = new Map<MediaStream, AnalyserNode>();

  /** Nova's inbound audio and the user's microphone, as published by the transport. */
  private realtimeNovaStream: MediaStream | null = null;
  private realtimeMicStream: MediaStream | null = null;

  /** Resolved on the first Realtime call and kept. See realtimeVoiceUi(). */
  private realtimeVoiceUiService: NovaRealtimeVoiceUiService | null = null;

  /** Likewise resolved on first use, and for the same reason. See realtimeVoiceUi(). */
  private realtimeSessionService: NovaRealtimeSessionService | null = null;
  private realtimeRelayService: NovaRealtimeRelayService | null = null;

  /**
   * FLIP-278. Whether this client should open Realtime voice, as the SERVER last reported it.
   *
   * Read once when the sheet opens rather than per toggle, so the path cannot change underneath a
   * call that is already running.
   *
   * FLIP-486. NULL until the server has actually answered, and null is not false. It used to start
   * false, so a voice tap that beat a slow context call - the API's first request after an idle
   * spell - quietly opened the legacy path, which has no "Connecting" state. Null now means "ask,
   * and say so while waiting"; see resolveVoicePathThenToggle.
   */
  private realtimeEnabled: boolean | null = null;

  /**
   * FLIP-486. True while a voice tap is waiting on the server's answer about which path to open.
   * `isVoiceActive` is true throughout (voiceState is not "off"), so the bar shows, and
   * voiceStatusLabel reads "Connecting…" for it.
   */
  private voicePathPending = false;

  /** The in-flight flag request, shared so a tap and the sheet-open load make one call, not two. */
  private realtimeFlag$: Observable<boolean | null> | null = null;

  private voicePathSubscription: Subscription | null = null;

  /** How long a voice tap waits for the server to say which path to open before giving up. */
  private static readonly VOICE_PATH_WAIT_MS = 20000;

  /**
   * Which engine the CURRENT call is running on.
   *
   * Distinct from `realtimeEnabled`, which is only what the next call would choose, and from
   * `realtimeVoice`, which stays null until the transport publishes its first view - there is a
   * window between minting a session and that first view where the WebRTC connection is live and
   * must still be torn down. Getting this wrong is precisely how a concurrency slot gets stranded.
   */
  private realtimePathActive = false;

  /**
   * FLIP-284. True once the peer connection is actually up.
   *
   * The voice-UI view is a BehaviorSubject that reports `listening` as soon as the service starts,
   * which is BEFORE the connection exists - so it cannot be used to tell "connecting" from
   * "connected", and the status bar would claim Nova was listening over a dead socket.
   */
  private realtimeConnected = false;

  /**
   * FLIP-284. When Nova's current audio began, or 0 when she is not making sound.
   *
   * Read by voiceStatusLabel, which will not say "Nova is speaking" until the transport reports
   * that audio has actually started - so the bar cannot claim she is talking over silence.
   */
  private realtimeAudioStartedAt = 0;

  /**
   * Built once, deterministically. The heights are deliberately uneven (a plain sine
   * reads as a mechanical ripple rather than the irregular wave in the design) and the
   * phase repeats every 8 bars so the pulse travels along the wave.
   */
  readonly waveBars: WaveBar[] = Array.from({ length: DICTATE_WAVE_BAR_COUNT }, (_, i) => ({
    heightPercent: 30 + 70 * Math.abs(Math.sin(i * 1.7)),
    delayMs: (i % 8) * 75,
  }));

  /**
   * Neutral until the context call says otherwise. Defaulting to a real band would colour the
   * header as a score the user may not have made - including for the frame before the call
   * resolves, and permanently if it fails.
   */
  bufferBand: BufferBand = "no-score";

  /**
   * What gets POSTED to Nova: the current conversation only, never the scrollback.
   *
   * Distinct from `thread`, which is what gets RENDERED and additionally holds earlier
   * conversations, dividers, nav toasts and the typing indicator. Loading history into this array
   * instead would re-send every past turn on every message for the rest of the sitting.
   */
  private conversation: ChatMessage[] = [];

  /**
   * When the current conversation was last touched, either direction. Past
   * `conversationIdleMinutes` of this, the next message begins a new conversation - matching what
   * the server independently decides when it stores the turn.
   */
  private lastInteractionAt = Date.now();

  /** Server-supplied; see NovaCompanionChatService.getConversationIdleMinutes. */
  private conversationIdleMinutes = 30;

  private opened = false;
  private dictateSeconds = 0;
  // Web Speech API isn't in TS's DOM lib yet - typed loosely rather than declaring a
  // full ambient SpeechRecognition interface for one internal field.
  private recognition: any = null;
  private dictateIntervalId: ReturnType<typeof setInterval> | null = null;
  /** Whatever was already typed when dictation started - the transcript is appended to it. */
  private dictateBaseText = "";
  /** Transcript from earlier restart cycles: `event.results` starts empty again after each. */
  private dictateCommitted = "";
  private dictateSessionText = "";
  private dictateUserStopped = false;
  private dictateSessionStartedAt = 0;
  private dictateSessionHadResult = false;
  private dictateFailedRestarts = 0;
  /**
   * FLIP-232. True while the recognizer is running AHEAD of the handover, during the tail of
   * Nova's last chunk. Anything it hears in this state is her, not the user, so results are
   * banked but never published - see maybePrewarmRecognizer.
   */
  private voiceWarmingRecognizer = false;
  /**
   * FLIP-232. Whatever the warm recognizer had transcribed at the handoff instant. Stripped from
   * the session transcript from then on, so Nova's own tail cannot reach the turn logic.
   */
  private dictateWarmupPrefix = "";
  // ── Voice mode (FLIP-158) ──
  /**
   * Bumped on every new spoken reply and whenever voice mode stops. Synthesis is a chain of
   * async calls, so a turn that was in flight when the user interrupted would otherwise come
   * back and start playing over whatever replaced it. Each step checks the id it started with
   * and abandons itself if it no longer matches.
   */
  private speechTurn = 0;
  /** Synthesised chunks waiting to play, in order. */
  private speechQueue: Blob[] = [];
  private currentAudio: HTMLAudioElement | null = null;
  /** Object URL backing currentAudio, kept so it can be revoked once playback ends. */
  private currentAudioUrl: string | null = null;
  /** True once every chunk of the current reply has been synthesised and queued. */
  private speechSynthesisComplete = false;
  /**
   * Pending end-of-utterance timer. Reset on every recognition result, so it only fires once the
   * user has actually stopped talking rather than paused mid-sentence.
   */
  private voiceEndTimerId: ReturnType<typeof setTimeout> | null = null;
  /** When the transcript last grew. The end-of-utterance window is measured from here. */
  private voiceLastResultAt = 0;
  /**
   * Set once per voice session after speech synthesis fails, so a broken voice back-end explains
   * itself once instead of either staying silent or repeating the same notice every turn.
   */
  private voiceSpeechFailureNotified = false;
  /**
   * One context for the whole voice session rather than one per listening turn: the mic tap is
   * released the moment the user stops talking, but Nova's playback still needs somewhere to be
   * analysed. Closed in stopVoiceMode.
   */
  private voiceAudioContext: AudioContext | null = null;
  /** Reads the microphone during the user's turn. */
  private voiceMicAnalyser: AnalyserNode | null = null;
  /** Reads Nova's audio during hers. Kept across chunks - each chunk plays through it. */
  private voicePlaybackAnalyser: AnalyserNode | null = null;
  /** The current chunk's tap into that analyser. Disconnected with the chunk that owns it. */
  private voicePlaybackSource: MediaElementAudioSourceNode | null = null;
  /** Whichever of the two the render loop is currently drawing, or null when it isn't running. */
  private voiceWaveAnalyser: AnalyserNode | null = null;
  private voiceMicStream: MediaStream | null = null;
  private voiceWaveFrameId: number | null = null;
  private voiceWaveLastFrameAt = 0;
  private voiceFrequencyBuffer: Uint8Array | null = null;
  private voiceTimeBuffer: Uint8Array | null = null;
  /**
   * Which analyser bin each bar reads, as a fractional index into the frequency data - bar N sits
   * between bins floor(p) and floor(p)+1 and blends them, so the wave stays smooth even where the
   * bars outnumber the bins. Rebuilt whenever the bar count or the analyser changes.
   */
  private voiceWaveBins: number[] = [];
  /** Watches the strip so the bar count follows its width. Null where ResizeObserver isn't. */
  private voiceWaveResizeObserver: ResizeObserver | null = null;
  /** Timestamp of the last frame whose level cleared VOICE_WAVE_SPEECH_RMS. */
  private voiceLastSignalAt = 0;
  /** Time-domain buffer for the microphone specifically - the wave may be reading playback. */
  private voiceMicTimeBuffer: Uint8Array | null = null;
  /**
   * The microphone's source node, and a silent sink for it.
   *
   * Both exist to keep the node ALIVE. A MediaStreamAudioSourceNode whose chain never reaches the
   * context's destination is eligible for collection, and once it goes the analyser hanging off it
   * reads zeros for ever - silently, because an analyser with nothing behind it looks exactly like
   * a quiet room. Holding the reference is the fix; the zero-gain connection to the destination is
   * the belt to its braces, since a graph that reaches the destination is pulled by definition.
   *
   * This was survivable while the tap was rebuilt on every listening turn. It stopped being
   * survivable when the microphone started having to live through Nova's turn as well, which is
   * precisely when barge-in needs to hear something.
   */
  private voiceMicSource: MediaStreamAudioSourceNode | null = null;
  private voiceMicSink: GainNode | null = null;
  /** When the user started talking over Nova, or 0. Barge-in needs it sustained, not momentary. */
  private voiceBargeInSince = 0;
  /** The last frame that cleared the gate - see VOICE_BARGE_IN_HANGOVER_MS. */
  private voiceBargeInLastLoudAt = 0;
  /**
   * Nova's own output level, read in the same frame as the microphone's. The reference signal the
   * barge-in gate is measured against - see VOICE_BARGE_IN_ECHO_RATIO.
   */
  private voicePlaybackTimeBuffer: Uint8Array | null = null;
  /**
   * The barge-in monitor's own timer. Deliberately NOT the waveform's render loop: interruption is
   * a core interaction and the wave is decoration, and hanging the first on the second meant that
   * any path where the wave failed to start took barge-in silently down with it.
   */
  private voiceBargeInMonitorId: ReturnType<typeof setInterval> | null = null;
  /** Resumes Nova if the interruption produced no words at all - see VOICE_INTERRUPT_RESUME_MS. */
  private voiceResumeTimerId: ReturnType<typeof setTimeout> | null = null;
  /** When Nova was paused, so the wait for words can be capped. */
  private voiceInterruptedAt = 0;
  /**
   * Milestones for one turn, for the latency log. See logTurnLatency.
   *
   * "3 seconds, then another 3 seconds" is a stopwatch reading of two halves that each contain
   * several things, and the halves are not the ones the code is divided into. These are.
   */
  private voiceTurnMarks: {
    finished: number;
    firstChunk: number;
    synthSent: number;
    serverTimings: TurnTimings | null;
  } = {
    finished: 0,
    firstChunk: 0,
    synthSent: 0,
    serverTimings: null,
  };
  /**
   * FLIP-230. When voice mode was switched on, and when the recogniser was actually ready.
   *
   * Session-scoped rather than per turn, because what they measure only happens once: the
   * microphone permission, the audio graph and the browser's recogniser all warm up at voice-mode
   * entry and stay warm. The ticket names that warm-up as one of four candidate causes for the
   * start-of-conversation lag, and it is the only one that cannot be seen from the server at all.
   *
   * Both zero outside a voice session.
   */
  private voiceSessionMarks: { opened: number; listeningReady: number } = {
    opened: 0,
    listeningReady: 0,
  };
  /** FLIP-230. Spoken turns completed this voice session, so turn 1 is identifiable in the log. */
  private voiceSessionTurnIndex = 0;
  /**
   * Distinguishes one send from the next, so a reply whose turn was interrupted cannot land in the
   * thread after the message that interrupted it. The same guard speechTurn provides for audio.
   */
  private replyTurn = 0;
  /**
   * Whether handleReply has already put this reply in the thread and the history.
   *
   * It lands as TEXT the moment the stream finishes, which is typically seconds before the audio
   * has reached the same point. So by the time anyone interrupts, the full reply is usually
   * already recorded - and an interruption has to rewrite that entry rather than add a second one
   * next to it.
   */
  private voiceReplyRecorded = false;
  /** When Nova started speaking, for the grace period before she can be cut off. */
  private voiceSpeakingSince = 0;
  /** localStorage.novaVoiceDebug - see voiceDebugSetting. Read once, not per frame. */
  private readonly voiceDebug = voiceDebugSetting("novaVoiceDebug") === "1";
  private voiceDebugLoggedAt = 0;
  /** Which chunk is playing. Advances in step with the queue, which is strictly in order. */
  private spokenChunkIndex = -1;

  /**
   * Sentences handed over for synthesis, in order. Appended to as the reply streams in rather
   * than computed once from a finished reply - that is the whole point of streaming, and the
   * reason synthesis of sentence one can start while sentence three is still being written.
   */
  private pendingSpokenChunks: string[] = [];

  /**
   * Where the synthesis loop stopped for want of a sentence, or null when it is running.
   *
   * The loop used to walk a fixed array and finish when it ran off the end. Now the end may
   * simply not have been written yet, so it parks here and the next chunk restarts it.
   */
  private parkedChunkIndex: number | null = null;

  /** Set when the reply has finished arriving. Until then, running out of chunks is not the end. */
  private speechStreamComplete = false;

  /** The reply as it accumulates. Everything downstream still needs the whole thing. */
  private streamedReply = "";

  /** How many sentences of the reply have been queued, so re-splitting does not re-queue them. */
  private queuedChunkCount = 0;

  private spokenFrameId: number | null = null;

  private scrollTimeoutId: ReturnType<typeof setTimeout> | null = null;
  /** FLIP-488. Stops the on-screen-keyboard watcher started in ngOnInit. */
  private stopKeyboardWatch: (() => void) | null = null;
  private repinAfterComposerResize = false;
  private lastAutoScrollAt = 0;
  private subscription = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<NovaCompanionChatComponent>,
    private chatService: NovaCompanionChatService,
    private router: Router,
    private zone: NgZone,
    // Used only by the waveform loop, which runs outside Angular precisely so it does NOT
    // schedule a global change-detection pass per frame. detectChanges() repaints this
    // component's subtree and nothing else.
    private cdr: ChangeDetectorRef,
    private launcherState: NovaCompanionLauncherStateService,
    // FLIP-276. Resolved lazily rather than injected, see realtimeVoiceUi().
    private injector: Injector,
    // FLIP-234. Optional so the component still constructs if it is ever opened without going
    // through novaCompanionChatDialogConfig() - docking then simply cannot unblock page scroll,
    // which is a degraded docked bar rather than a component that fails to open.
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    private dialogData: NovaCompanionChatDialogData | null,
    // FLIP-275. Root-provided, and deliberately not owned here: answering the model's tool calls
    // has to outlive this component (see its own header comment). This component only renders what
    // a finished call left behind.
    private realtimeToolCalls: NovaRealtimeToolCallService,
    // FLIP-311. A typed turn can begin a walkthrough; the steps arrive on the reply's final frame
    // and are handed here, so it runs through the same state a spoken one does.
    private walkthrough: NovaWalkthroughService,
    // FLIP-389. N02's first-use panel: owns whether it shows and what closing it means.
    public n02: N02HowNovaWorksService,
    // FLIP-390. N03A's prompt at the microphone.
    private n03a: N03ADictateMessageService,
    // FLIP-391. N03B's prompt at Voice Chat.
    private n03b: N03BTalkWithNovaService,
    // FLIP-392. N04's prompt at the coloured header.
    private n04: N04SafetyBufferHeaderService
  ) {}

  /**
   * FLIP-389. Whether N02's first-use panel replaces the thread. Hidden while voice mode runs (the
   * thread is hushed then anyway) without resolving the lesson - it is still there afterwards.
   */
  get showFirstUse(): boolean {
    return this.n02.isVisible && !this.isVoiceActive;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.chatService.getBufferBand().subscribe((band) => {
        this.bufferBand = band;
        this.updateFirstUseLessons();
      })
    );
    // FLIP-390/391/392: the Nova lessons follow N02's panel, and one another - re-evaluate whenever
    // any of them moves on.
    for (const signal$ of [this.n02.visible$, this.n02.settled$, this.n03a.done$, this.n03b.done$]) {
      this.subscription.add(signal$.subscribe(() => this.updateFirstUseLessons()));
    }
    // FLIP-275. What a Realtime tool call resolved to, rendered the same way the text path renders
    // a [[NAV:...]] tag. Nova still never navigates anyone: this pushes the offer into the thread
    // and the user chooses to tap it (FLIP-219).
    this.subscription.add(
      this.realtimeToolCalls.outcome$.subscribe((outcome) => this.handleToolCallOutcome(outcome))
    );
    // The minimised launcher (NovaCompanionFabComponent's circle) asking to be expanded again. It
    // cannot call restoreVoice() directly - it holds no reference to this component - and it must
    // not call dialog.open(), which would stack a second Nova over the one already running.
    this.subscription.add(
      this.launcherState.restoreRequested$.subscribe(() => this.restoreVoice())
    );
    // FLIP-488. Outside the zone: visualViewport fires continuously while the keyboard animates, and
    // none of it touches template state - it only toggles a class and two CSS variables on <html>.
    this.zone.runOutsideAngular(() => {
      this.stopKeyboardWatch = watchKeyboardViewport(window as unknown as KeyboardViewportWindow, () =>
        // The sheet just gained or lost the height the keyboard takes, so the newest bubble may have
        // slid out of view. The size change lands a frame later, hence the deferral in the scroller.
        this.scheduleScrollToBottom("auto")
      );
    });
    this.openProactive();
    // FLIP-389: the first opening of Nova from any route shows N02, if it is unresolved.
    this.n02.onNovaOpened();
    // FLIP-390: the chat is the host of N03A's prompt for as long as it is open.
    this.n03a.enter();
    this.n03b.enter();
    this.n04.enter();
    this.updateFirstUseLessons();
  }

  /**
   * FLIP-390/391/392. Once N02's first-use panel is closed (or was never going to show - `settled`, so
   * nothing flashes up just before it takes over) the chat is showing, and the Nova lessons run in
   * order, each starting only when the one before it is done:
   *   N03A at the microphone -> N03B at Voice Chat -> N04 at the coloured header.
   * N04 also needs a coloured header (a Score today - the band is green / amber / red rather than the
   * neutral "no-score"); it stays deferred until there is one. A lesson already resolved on an earlier
   * session counts as done, so the sequence moves straight past it.
   */
  private updateFirstUseLessons(): void {
    const chatShowing = this.n02.isSettled && !this.n02.isVisible;
    this.n03a.setReady(chatShowing);
    this.n03b.setReady(chatShowing && this.n03a.isDone);
    this.n04.setReady(chatShowing && this.n03a.isDone && this.n03b.isDone && this.bufferBand !== "no-score");
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    // FLIP-389: closing Nova while N02 is visible dismisses it permanently.
    this.n02.onNovaClosed();
    this.n03a.leave();
    this.n03b.leave();
    this.n04.leave();
    if (this.scrollTimeoutId) clearTimeout(this.scrollTimeoutId);
    // Also clears the <html> class and variables, so closing Nova with the keyboard up cannot leave
    // the bottom nav hidden.
    this.stopKeyboardWatch?.();
    this.stopKeyboardWatch = null;
    // The view query that owns this doesn't fire again on destroy.
    this.voiceWaveResizeObserver?.disconnect();
    this.voiceWaveResizeObserver = null;
    // Before stopDictation: this also stops playback and revokes the outstanding object URL,
    // so closing mid-sentence doesn't leave Nova talking to an unmounted component.
    this.stopVoiceMode();
    // Belt and braces on top of the undock stopVoiceMode() just did: the FAB and the header badge
    // read this flag, and they outlive this component. Leaving it set on any path out of here
    // would hide both of them permanently, with no bar left to open the chat from.
    this.launcherState.setVoiceDocked(false);
  }

  get hasText(): boolean {
    return this.inputText.trim().length > 0;
  }

  /**
   * The sheet's close (X). While N02's first-use panel is up it closes the panel and reveals the
   * chat (FLIP-389) - Nova stays open; otherwise it closes Nova.
   */
  onSheetClose(): void {
    if (this.showFirstUse) {
      this.n02.onClosePressed();
      return;
    }
    this.close();
  }

  /** x button (base-modal's built-in close) dismisses the dialog. */
  close(): void {
    this.stopVoiceMode();
    this.dialogRef.close();
  }

  private openProactive(): void {
    if (this.opened) return;
    this.opened = true;

    // FLIP-278. Fetched here rather than when voice is toggled, so the answer is already in hand
    // by the time the user reaches for the button: asking then would put a round trip between the
    // tap and anything happening, on the one interaction where the user is about to start talking.
    //
    // FLIP-486. Its own request, no longer part of the forkJoin below: that waits for the slowest
    // of four calls, so a slow history load held the voice answer back with it.
    this.subscription.add(this.requestRealtimeFlag().subscribe());

    // History and greeting are fetched together so their ORDER is deterministic. Subscribing
    // separately would let the greeting land first on a slow history call and put "Morning, Jen"
    // above conversations from last week.
    this.subscription.add(
      forkJoin([
        this.chatService.getChatHistory(),
        this.chatService.getUserFirstName(),
        this.chatService.getConversationIdleMinutes(),
      ]).subscribe(([history, firstName, idleMinutes]) => {
        this.conversationIdleMinutes = idleMinutes;

        const { previous, current } = splitCurrentConversation(
          groupHistoryIntoConversations(history),
          idleMinutes,
          Date.now()
        );

        // Earlier conversations first, each under its own heading. These go into `thread` and
        // NOT into `conversation` - they are what the reader can scroll back to, not what Nova
        // is sent.
        for (const group of previous) {
          this.thread.push({ kind: "divider", text: group.heading });
          for (const turn of group.turns) {
            this.pushStoredTurn(turn);
          }
        }

        if (current) {
          // Deliberately no divider and no greeting: this is the conversation the user is still
          // in. A heading would announce a boundary that did not happen, and greeting someone
          // mid-exchange reads as Nova having forgotten the last thing they said.
          //
          // These turns DO go into `conversation` - unlike the groups above, they are what has
          // been said since this conversation began, which is exactly what the next send must
          // carry. Raw content, matching what the live path stores: the tags are stripped for
          // display only.
          for (const turn of current.turns) {
            this.pushStoredTurn(turn);
            this.conversation.push({ role: turn.role, content: turn.content });
          }

          // From the last stored turn, not from now - the idle clock measures silence in the
          // conversation, and the sheet being closed for ten minutes is still ten minutes of it.
          const lastTurn = current.turns[current.turns.length - 1];
          this.lastInteractionAt = parseApiTimestamp(lastTurn.occurredAt).getTime();
        } else {
          this.thread.push({
            kind: "message",
            role: "assistant",
            text: `${this.getGreeting(firstName)} What's on your mind today?`,
          });
        }

        // Instant: the sheet is still animating in, so a smooth scroll on first
        // paint reads as a glitch. Doubly so with history above - a smooth scroll
        // would visibly travel down through weeks of old messages.
        this.scheduleScrollToBottom("auto");

        // Openers only open something. Requested here rather than in parallel with the history
        // call because only the answer above says whether they are wanted: firing both at once
        // would either flash them in before history landed, or spend an LLM call on chips that
        // are hidden the moment it returns.
        if (!current) this.loadOpeningChips();
      })
    );
  }

  private loadOpeningChips(): void {
    this.chipsLoading = true;
    this.subscription.add(
      this.chatService.getOpeningChips().subscribe((chips) => {
        this.chipsLoading = false;
        this.chips = chips.length >= 3 ? chips : FALLBACK_CHIPS;
        this.showChips = true;
        // The chips render inside .nova-companion-chat__thread, so revealing
        // them changes the thread's height.
        this.scheduleScrollToBottom("auto");
      })
    );
  }

  /**
   * Renders a stored turn into the thread the way the live path renders a fresh one.
   *
   * Stored assistant turns are the RAW reply, tags and all - the server records what the model
   * said. So the scrollback has to do what `handleReply` does: strip the tags out of the bubble,
   * and put back the nav toast the tag stood for. Without the strip the user is shown
   * "[[NAV:Score]]"; without the toast a pill vanishes the moment the sheet is reopened, even
   * though the message offering it is still right there.
   *
   * Tags are stripped in the live path's order, since both patterns are anchored to the end.
   *
   * The safety-escalation tag is stripped but NOT acted on. `handleSafetyEscalation` notifies the
   * dev team that crisis resources were surfaced; that already happened when this turn was live,
   * and firing it again would re-raise one historic escalation on every reopen, for ever.
   */
  private pushStoredTurn(turn: ChatHistoryTurn): void {
    if (turn.role !== "assistant") {
      this.thread.push({ kind: "message", role: turn.role, text: turn.content });
      return;
    }

    const { clean: withoutSafety } = this.parseSafetyEscalation(turn.content);
    const { clean, nav, navTitle } = this.parseNav(withoutSafety);

    this.thread.push({ kind: "message", role: "assistant", text: clean });

    // No resolved target on this path, and deliberately not looked up. A stored turn is replayed
    // from text alone - there is no stream frame behind it - and the ids a title would resolve to
    // are reassigned by a content reseed, so resolving an old title client-side could route
    // somebody to a DIFFERENT lesson than the one Nova named. The toast still carries the title,
    // so it reads correctly; clicking it opens the Learn homepage rather than a wrong lesson.
    if (nav) this.pushNavToast(nav, undefined, navTitle);
  }

  /**
   * Appends the toast for a [[NAV:X]] tag. Shared by the live and restored paths so a tag cannot
   * render one way when Nova says it and another way when it is read back.
   *
   * `nav` is an unvalidated tag value; the label falls back to it, and `onNavChipClicked` is what
   * makes an unroutable target harmless.
   */
  private pushNavToast(nav: string, resolved?: NovaResolvedNav, navTitle?: string | null): void {
    // Label preference, most specific first: what the server actually resolved, then the title the
    // model named, then the section. Naming the destination is the point of a targeted tag - a
    // toast reading "Explore: Learn" when Nova has just recommended one lesson throws away the
    // specificity the user was given in the sentence above it.
    const label =
      resolved?.title ?? navTitle ?? NOVA_NAV_LABELS[nav as NovaNavTarget] ?? nav;

    this.thread.push({
      kind: "nav",
      text: label,
      navTarget: nav as NovaNavTarget,
      navResolved: resolved,
    });
  }

  private getGreeting(firstName: string): string {
    const hour = new Date().getHours();
    const timeOfDay = hour < 12 ? "Morning" : hour < 17 ? "Afternoon" : "Evening";
    return firstName ? `${timeOfDay}, ${firstName}.` : `${timeOfDay}.`;
  }

  onChipClicked(label: string): void {
    this.showChips = false;
    this.sendUserMessage(label);
  }

  onSend(): void {
    const text = this.inputText.trim();
    this.inputText = "";
    this.resetTextareaHeight();
    if (!text || this.sending) return;
    this.showChips = false;
    this.sendUserMessage(text);
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
    }
  }

  /** Textarea auto-expand, capped like the handoff's inputBox listener. */
  onInputResize(): void {
    const el = this.inputBoxRef?.nativeElement;
    if (!el) return;

    // Sample both of these BEFORE mutating the height: collapsing to "auto"
    // changes .modal-body's scroll metrics, so isNearBottom() would be measuring
    // a layout the user never saw.
    const wasAtBottom = this.baseModal?.isNearBottom() ?? false;
    const previousHeight = el.style.height;

    el.style.height = "auto";
    // scrollHeight excludes the border, but the element is border-box, so add it
    // back rather than letting the SINGLE_LINE_HEIGHT floor paper over the gap.
    const newHeight = Math.min(
      Math.max(el.scrollHeight + INPUT_BORDER_Y, SINGLE_LINE_HEIGHT),
      MAX_INPUT_HEIGHT
    );
    el.style.height = newHeight + "px";
    // Only scrollable once we stop growing, otherwise text past the cap would be
    // unreachable (the CSS keeps overflow-y hidden so the box never shows a
    // scrollbar while it's still expanding).
    el.style.overflowY = newHeight >= MAX_INPUT_HEIGHT ? "auto" : "hidden";
    this.isInputMultiline = newHeight > SINGLE_LINE_HEIGHT;

    // A growing composer shrinks .modal-body. Because scrollHeight - clientHeight
    // grows, the browser does NOT clamp scrollTop, so the newest bubble genuinely
    // slides out of view. Re-pin - but the input has a CSS height transition, so
    // the shrink isn't done yet; onComposerTransitionEnd does the actual scroll.
    this.repinAfterComposerResize = wasAtBottom && previousHeight !== newHeight + "px";
  }

  /**
   * Re-pins the thread once the composer's height transition has actually
   * finished (nova-companion-chat.component.scss transitions height, so a
   * setTimeout(0) here would measure the pre-transition layout and land short).
   */
  onComposerTransitionEnd(event: TransitionEvent): void {
    if (event.propertyName !== "height" || !this.repinAfterComposerResize) return;
    this.repinAfterComposerResize = false;
    this.baseModal?.scrollToBottom("auto");
  }

  private resetTextareaHeight(): void {
    const el = this.inputBoxRef?.nativeElement;
    if (el) {
      el.style.height = SINGLE_LINE_HEIGHT + "px";
      el.style.overflowY = "hidden";
    }
    this.isInputMultiline = false;
    // Don't let a pending re-pin leak into the collapse transition we just started.
    this.repinAfterComposerResize = false;
  }

  /**
   * Coalesces back-to-back thread pushes into one scroll (sendUserMessage pushes
   * the user message and the typing indicator in the same tick).
   *
   * setTimeout(0) is a macrotask, so it runs after zone's onMicrotaskEmpty ->
   * ApplicationRef.tick() has committed the new *ngFor nodes to the DOM. This
   * matches how the rest of the app defers scrolling (see safety-plan.component).
   */
  private scheduleScrollToBottom(behavior: ScrollBehavior = "smooth"): void {
    if (this.scrollTimeoutId) clearTimeout(this.scrollTimeoutId);
    this.lastAutoScrollAt = Date.now();
    this.scrollTimeoutId = setTimeout(() => {
      this.scrollTimeoutId = null;
      this.baseModal?.scrollToBottom(behavior);
    }, 0);
  }

  /**
   * Ends the current conversation and starts a new one when the sheet has sat idle past the
   * threshold.
   *
   * This exists because the sheet can stay open indefinitely. Someone who types, wanders off for
   * an hour and comes back is starting a new conversation by the same rule the server applies when
   * it stores the turn - and without this the client would post the stale conversation as though
   * no time had passed, so Nova would reply mid-thought to something said an hour ago.
   *
   * Only `conversation` is cleared. The turns stay on screen, now above a divider, which is what
   * makes the boundary something the reader sees rather than something that silently happened.
   */
  private startNewConversationIfIdle(): void {
    if (this.conversation.length === 0) return;

    const idleMs = this.conversationIdleMinutes * 60 * 1000;
    if (Date.now() - this.lastInteractionAt < idleMs) return;

    this.conversation = [];
    this.thread.push({ kind: "divider", text: "New conversation" });
  }

  /**
   * Whether an incoming message should pull the thread down. False once the user
   * has scrolled up to re-read history, so a reply never yanks them away from it.
   */
  private shouldFollowThread(): boolean {
    if (Date.now() - this.lastAutoScrollAt < SMOOTH_SCROLL_SETTLE_MS) return true;
    return this.baseModal?.isNearBottom() ?? true;
  }

  private sendUserMessage(text: string): void {
    this.startNewConversationIfIdle();

    this.thread.push({ kind: "message", role: "user", text });
    this.conversation.push({ role: "user", content: text });
    this.lastInteractionAt = Date.now();
    this.thread.push({ kind: "typing" });
    this.sending = true;
    // Ungated: the user just sent this, so always bring it into view. One call
    // covers both pushes above - scheduleScrollToBottom coalesces them.
    this.scheduleScrollToBottom("smooth");

    this.streamedReply = "";
    this.queuedChunkCount = 0;
    this.voiceReplyRecorded = false;
    // Claimed before the request goes out, so an interruption can invalidate it by bumping the
    // counter - the same guard speechTurn already provides for audio.
    const turn = ++this.replyTurn;

    this.subscription.add(
      // isVoiceActive rather than a stored flag: what matters is whether this reply will be
      // spoken, and voice mode can be switched on between one turn and the next.
      // The walkthrough the browser is holding is reported with the message, so the server can tell
      // the model where the user is. Null when there is none.
      this.chatService.sendMessageStream(this.conversation, this.isVoiceActive, this.walkthrough.snapshot()).subscribe({
        next: (chunk) => this.handleReplyChunk(chunk, turn),
        error: () => {
          if (turn !== this.replyTurn) return;
          this.removeTyping();
          this.thread.push({
            kind: "message",
            role: "assistant",
            text: "I'm having trouble connecting right now, mind trying that again?",
          });
          this.sending = false;
          // FLIP-158. The failure is already on screen as a message; hand the turn straight
          // back rather than leaving voice mode stuck on "thinking" forever.
          if (this.voiceState === "thinking") this.resumeListening();
          if (this.shouldFollowThread()) this.scheduleScrollToBottom("smooth");
        },
      })
    );
  }

  /**
   * One frame of a streamed reply.
   *
   * The reply is assembled here and handed to handleReply whole, so everything downstream - tag
   * parsing, the nav toast, the escalation hook, the scrollback - behaves exactly as it did when
   * replies arrived in one piece. What streaming changes is that the SPEAKING starts long before
   * that, sentence by sentence, which is the point of the exercise.
   */
  private handleReplyChunk(chunk: ReplyChunk, turn: number): void {
    // Superseded: the user interrupted this reply and has already said something else. Without
    // this the abandoned stream would keep feeding speakStreamedSentences, which would find voice
    // mode not in "speaking" and open a whole second spoken turn on top of the live one.
    if (turn !== this.replyTurn) return;

    if (chunk.replace !== undefined && chunk.replace !== null) {
      // Discard, do not append. This is the refusal path, and a fallback carrying crisis
      // resources bolted onto the end of half an answer is worse than either half alone.
      this.stopPlayback();
      this.streamedReply = chunk.replace;
      this.queuedChunkCount = 0;
    } else if (chunk.delta) {
      this.streamedReply += chunk.delta;
      this.speakStreamedSentences(false);
    }

    if (chunk.done) {
      // FLIP-230. Only the final frame carries the breakdown, and it has to be captured before
      // finishSpokenTurn, which is what eventually logs the turn.
      if (chunk.timings) this.voiceTurnMarks.serverTimings = chunk.timings;

      // Flush first: the tail sentence is held back until now, because until the reply ends
      // there is no way to know it is a whole sentence rather than the start of a longer one.
      this.speakStreamedSentences(true);
      this.finishSpokenTurn();

      // The resolved nav target rides the same final frame as the timings, for the same reason:
      // the tag it comes from is the last thing the model writes.
      this.handleReply(this.streamedReply, chunk.nav);

      // What the model asked of the walkthrough this turn, applied to the state this browser holds
      // - before a new one is begun, since a turn may end one and start another.
      this.walkthrough.applyEvents(chunk.walkthroughEvents);

      // A walkthrough this turn began. After handleReply so the reply is on screen first.
      if (chunk.walkthroughSteps?.length) {
        this.walkthrough.beginFromChat(chunk.walkthroughSteps, chunk.walkthroughNextArea ?? null);
      }
    }
  }

  /**
   * Hands over whatever whole sentences have arrived since last time.
   *
   * The last sentence is held back unless flushing: the splitter merges short sentences into
   * their neighbours, so a fragment is only known to be final once something follows it. Tags are
   * stripped on every pass rather than only at the end - they always arrive last, so before the
   * flush they sit in the held-back tail either way, and stripping unconditionally means a
   * half-arrived tag can never be spoken.
   */
  private speakStreamedSentences(flush: boolean): void {
    if (!this.isVoiceActive) return;
    // FLIP-278, concern C8. A message TYPED while voice mode is open still goes out with
    // `voice:true` - the reply is written to be heard, and people genuinely do type mid-call. On
    // the Realtime path it must not also be pushed through /api/Nova/Speak: Nova already has a
    // voice on that path, and synthesising the same reply here would have her answer twice at
    // once, over WebRTC and through the legacy audio element.
    if (this.realtimePathActive) return;

    const { clean: withoutSafety } = this.parseSafetyEscalation(this.streamedReply);
    const { clean } = this.parseNav(withoutSafety);
    const chunks = splitIntoSpeechChunks(clean);
    const available = flush ? chunks.length : speakableChunkCount(chunks);
    if (available <= this.queuedChunkCount) return;

    for (let index = this.queuedChunkCount; index < available; index++) {
      // The first sentence opens the turn. Anything before this point was still "thinking".
      if (this.voiceState !== "speaking") {
        if (this.voiceTurnMarks.firstChunk === 0) this.voiceTurnMarks.firstChunk = Date.now();
        this.beginSpokenTurn();
        // Parked at the sentence about to be queued, so queueing it is what starts the loop. A
        // separate kick for the first chunk would be a second code path doing the same job - and
        // the one that gets forgotten the next time this changes.
        this.parkedChunkIndex = 0;
      }
      this.queueSpokenChunk(chunks[index]);
    }
    this.queuedChunkCount = available;
  }

  private handleReply(raw: string, resolvedNav?: NovaResolvedNav): void {
    this.removeTyping();
    const { clean: withoutSafety, triggered } = this.parseSafetyEscalation(raw);
    const { clean, nav, navTitle } = this.parseNav(withoutSafety);
    this.conversation.push({ role: "assistant", content: raw });
    this.thread.push({ kind: "message", role: "assistant", text: clean });
    this.voiceReplyRecorded = true;

    // Nova answering counts as interaction: the idle clock measures silence in the conversation,
    // not just how long since the person last typed.
    this.lastInteractionAt = Date.now();
    // A link the model gave through navigate_to carries no tag, so it is read from the resolved
    // target. It wins over a tag: it is the validated one.
    const target = novaNavTargetOfResolved(resolvedNav) ?? nav;
    if (target) this.pushNavToast(target, resolvedNav, navTitle);
    if (triggered) this.handleSafetyEscalation();
    this.sending = false;
    // FLIP-158. Speech is NOT started here any more - it started sentences ago, as the reply
    // streamed in. See speakStreamedSentences, which also does the tag-stripping this used to,
    // so the [[NAV:...]] / [[SAFETY_ESCALATION_TRIGGERED]] markers are still never read aloud.
    // The nav toast and escalation hook above still fire from the parsed values.
    //
    // The exception is a reply that produced no speakable text at all - an empty reply, or one
    // that was nothing but tags. Nobody is going to hand the turn back in that case.
    if (this.voiceState === "thinking") this.resumeListening();
    // Gated: if the user scrolled up to re-read history while waiting, leave them
    // there rather than yanking them down. Covers the bubble and the nav chip.
    if (this.shouldFollowThread()) this.scheduleScrollToBottom("smooth");
  }

  /**
   * Navigate to the suggested route when the user clicks a nav toast. The target comes from
   * the clicked thread item rather than component state, so clicking a toast further up the
   * conversation goes where that toast says.
   *
   * `target` is an unvalidated tag value (see ParsedNav) - a model-invented destination has
   * no route, and must be a no-op rather than a navigation to `/undefined`.
   */
  onNavChipClicked(target?: NovaNavTarget, resolved?: NovaResolvedNav): void {
    // A resolved target wins, and only ever routes DEEPER into the section it belongs to, so the
    // fallback below stays correct if it is unusable. Everything unresolved - an invented lesson
    // title, a tag from before this shipped, a restored turn with no stream frame behind it -
    // lands on the section, which is exactly where a bare [[NAV:Learn]] has always gone.
    if (resolved) {
      const deep = novaResolvedNavRoute(resolved);
      if (deep) {
        this.close();
        this.router.navigate([deep.route], deep.queryParams ? { queryParams: deep.queryParams } : {});
        return;
      }
    }

    if (!target) return;
    const route = NOVA_NAV_ROUTES[target];
    if (!route) return;
    this.close();
    this.router.navigate([route]);
  }

  private removeTyping(): void {
    this.thread = this.thread.filter((item) => item.kind !== "typing");
  }

  /**
   * Reads the [[NAV:X]] tag the system prompt is instructed to append, and strips it.
   *
   * Detection and removal must agree about WHERE a tag may appear. They used not to: the match was
   * unanchored but the replace ended in `$`, so a tag anywhere other than the very end was detected
   * - firing the toast - and then left in the text for the user to read. The prompt asks for the
   * tag last, and the model usually complies, which is exactly what made the mismatch easy to miss:
   * it only surfaces when the model orders the two tags the other way round, or writes a closing
   * line after one. Stripping globally removes the ordering dependency altogether.
   *
   * Replaced with a space rather than "" because a tag is not always terminal - splicing one out of
   * mid-sentence would otherwise weld the words either side of it together.
   */
  private parseNav(text: string): ParsedNav {
    // Two shapes, one pass. The bare [[NAV:Score]] form, and the targeted
    // [[NAV:Learn:lesson:Finding your baseline]] form the catalogue made possible.
    //
    // The title group is [^\]]+ rather than \w+, and that is the whole reason this regex was
    // rewritten: real titles carry spaces, commas, ampersands and an asterisk ("How to use flip*",
    // "Rest, recovery & regulation"). Under the old word-character pattern a targeted tag matched
    // NOTHING - so it fired no toast AND was left in the bubble for the user to read, which is the
    // same detect/strip mismatch documented above, arriving by a different route.
    const match = text.match(/\[\[NAV:(\w+)(?::(?:lesson|chapter):([^\]]+))?\]\]/i);
    const clean = text
      .replace(/\s*\[\[NAV:\w+(?::(?:lesson|chapter):[^\]]+)?\]\]\s*/gi, " ")
      .trim();

    return {
      clean,
      nav: match ? match[1] : null,
      navTitle: match && match[2] ? match[2].trim() : null,
    };
  }

  /** Reads the [[SAFETY_ESCALATION_TRIGGERED]] tag and strips it. Global for the reason in parseNav
   * - and the stakes are higher here, since the message that carries this tag is the one showing
   * someone crisis numbers. */
  private parseSafetyEscalation(text: string): ParsedSafetyEscalation {
    const triggered = /\[\[SAFETY_ESCALATION_TRIGGERED\]\]/.test(text);
    const clean = text.replace(/\s*\[\[SAFETY_ESCALATION_TRIGGERED\]\]\s*/g, " ").trim();
    return { clean, triggered };
  }

  /**
   * Fires whenever Nova reports it surfaced crisis-escalation resources this turn. Per
   * product decision (the FLIP-173 handoff's README, citing nova-data-handover.md §8b),
   * the dev team must be notified when this happens. No real notification mechanism
   * exists yet - this is the single, clearly-marked placeholder hook, not silently
   * dropped behaviour.
   */
  /**
   * Renders what a Realtime tool call resolved to (FLIP-275).
   *
   * The answer to the model has already gone back over the data channel by the time this runs -
   * that is mandatory and happens in NovaRealtimeToolCallService whether or not this component
   * exists. This is only the part the user can see, and for voice that is only crisis resources:
   * a spoken session offers no navigation link, so there is no toast to push.
   */
  private handleToolCallOutcome(outcome: NovaRealtimeToolCallOutcome): void {
    if (outcome.safetyEscalationTriggered) {
      this.handleSafetyEscalation();
    }
  }

  private handleSafetyEscalation(): void {
    // eslint-disable-next-line no-console
    console.warn(
      "[NovaCompanionChat] Safety escalation resources were surfaced to the user this turn. " +
        "Per product decision, the dev team must be notified when this happens. " +
        "No real notification mechanism exists yet - this is a placeholder hook."
    );
    // TODO (blocked on backend): notify the dev team here once a real mechanism exists.
  }

  // ── Voice dictation (Web Speech API) ──
  // Populates the input field only - the user must still press send explicitly. See
  // this component's header comment for why the handoff's auto-send-on-end behaviour
  // isn't ported.
  /**
   * The microphone button. Using it while N03A's prompt is up is the genuine use (FLIP-390), which
   * completes the lesson - the prompt itself is shown by the chat's lesson sequence, not by this tap.
   */
  onMicSelected(): void {
    this.n03a.onTargetUsed("dictation");
    this.toggleDictation();
  }

  toggleDictation(): void {
    if (this.isListening) {
      this.stopDictation();
      return;
    }

    const SpeechRecognitionCtor =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      // eslint-disable-next-line no-alert
      alert("Dictation isn't supported in this browser - try Chrome.");
      return;
    }

    this.showChips = false;
    // Flipped synchronously rather than from onstart: start() only resolves once the
    // browser has granted mic access, which on first use sits behind a permission prompt.
    // Waiting for onstart left the mic button looking dead, so people pressed it again -
    // and that second press landed on the stop branch above. Hence "click it twice".
    this.isListening = true;
    this.dictateUserStopped = false;
    this.dictateFailedRestarts = 0;
    this.dictateCommitted = "";
    this.dictateSessionText = "";
    // Preserve anything already typed - dictation appends to it rather than replacing it.
    const existing = this.inputText.replace(/\s+$/, "");
    this.dictateBaseText = existing ? existing + " " : "";
    this.dictateSeconds = 0;
    this.dictateTimerLabel = formatDictateTime(0);

    const recognition = new SpeechRecognitionCtor();
    this.recognition = recognition;
    recognition.lang = "en-AU";
    recognition.interimResults = true;
    // The recognizer has to survive the natural pauses inside a sentence. With
    // continuous = false the browser ends the session at the first one and everything
    // spoken after it is lost - that was the "only picks up part of what I'm saying".
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    // Every handler goes through zone.run(). SpeechRecognition is not zone-patched (it
    // isn't in zone.js's list, and these are on* properties rather than addEventListener),
    // so without this none of the state these callbacks write triggers change detection:
    // the overlay never appeared, the transcript never reached the textarea, and the
    // interval below - scheduled from inside an unpatched callback, so created in the
    // root zone - never repainted the timer.
    recognition.onstart = () => this.zone.run(() => this.onDictationStart());
    recognition.onresult = (event: any) => this.zone.run(() => this.onDictationResult(event));
    recognition.onend = () => this.zone.run(() => this.onDictationEnd());
    recognition.onerror = (event: any) => this.zone.run(() => this.onDictationError(event));

    this.startRecognition();
  }

  private startRecognition(): void {
    if (!this.recognition) return;
    this.dictateSessionStartedAt = Date.now();
    this.dictateSessionHadResult = false;
    try {
      this.recognition.start();
    } catch {
      // InvalidStateError: a restart that raced the previous session's teardown. The
      // recognizer is already running, which is exactly what we wanted.
    }
  }

  private onDictationStart(): void {
    // FLIP-230. The first onstart of a voice session is the recogniser actually becoming ready,
    // which is the only observable end to the microphone-and-recogniser warm-up the ticket asks
    // about. Guarded to the first: restarts re-enter this constantly and would keep overwriting a
    // mark whose whole meaning is "the session was ready at this moment".
    if (this.voiceSessionMarks.opened && !this.voiceSessionMarks.listeningReady) {
      this.voiceSessionMarks.listeningReady = Date.now();
      if (this.voiceDebug) {
        // eslint-disable-next-line no-console
      }
    }

    // Restarts re-enter this; only the first session starts the clock, so the timer
    // reports total dictation time rather than resetting on every browser cut-in.
    if (this.dictateIntervalId) return;
    this.dictateSeconds = 0;
    this.dictateTimerLabel = formatDictateTime(0);
    this.dictateIntervalId = setInterval(
      () =>
        this.zone.run(() => {
          this.dictateSeconds++;
          this.dictateTimerLabel = formatDictateTime(this.dictateSeconds);
        }),
      1000
    );
  }

  private onDictationResult(event: any): void {
    this.dictateSessionHadResult = true;
    this.dictateFailedRestarts = 0;
    this.dictateSessionText = this.stripWarmupPrefix(
      Array.from(event.results as ArrayLike<any>)
        .map((result: any) => result[0].transcript)
        .join("")
    );
    // FLIP-232. The recognizer is warm but the floor is still Nova's, so this is her voice coming
    // back through the microphone. Banked above so the handoff knows what to discard, and
    // published nowhere: not to the composer, not to the turn timers.
    if (this.voiceWarmingRecognizer) return;
    this.inputText = this.dictateBaseText + this.composeDictatedText();
    // FLIP-158. Interim results fire continuously while the user talks, so restarting the timer
    // here means it only reaches zero once they have actually stopped. Text dictation is
    // untouched - it has a send button, so it has no turn to hand over.
    if (this.voiceState === "listening" || this.voiceState === "interrupted") {
      // Words arrived, so this was not a cough: the decision now belongs to the end-of-utterance
      // timer, which can tell a backchannel from a real turn.
      this.clearVoiceResumeTimer();
      this.restartVoiceEndTimer();
    }
  }

  /**
   * Arms (or re-arms) the end-of-utterance timer. See VOICE_END_OF_UTTERANCE_MS for why voice
   * mode times its own silence rather than waiting for the browser to call the turn.
   */
  private restartVoiceEndTimer(): void {
    this.voiceLastResultAt = Date.now();
    this.armVoiceEndTimer(this.voiceEndWindowMs(this.composeDictatedText()));
  }

  /**
   * How long to wait on this particular transcript, from most-finished to least.
   *
   * Three signals, ordered by what they are worth. Terminal punctuation is the strongest - the
   * recognizer only adds it where it believes a sentence closed. A trailing function word is the
   * strongest signal of the opposite. Everything else takes the default.
   */
  private voiceEndWindowMs(spoken: string): number {
    const trimmed = spoken.trim();
    if (endsMidThought(trimmed)) return VOICE_END_OF_UTTERANCE_TRAILING_MS;
    if (/[.!?]$/.test(trimmed)) return VOICE_END_OF_UTTERANCE_SETTLED_MS;
    return VOICE_END_OF_UTTERANCE_MS;
  }

  private armVoiceEndTimer(delay: number): void {
    this.clearVoiceEndTimer();
    this.voiceEndTimerId = setTimeout(() => this.zone.run(() => this.onVoiceEndTimer()), delay);
  }

  /**
   * Decides whether the user has actually finished, now the transcript has been quiet a while.
   *
   * The transcript alone is a poor witness: the recogniser stops emitting the moment someone
   * pauses to think, so "no new words" was being read as "done talking" and cutting people off
   * mid-sentence. The microphone knows better, and since the waveform is already measuring it
   * (see VOICE_WAVE_SPEECH_RMS), asking costs nothing: while it still hears a voice, the turn
   * stays open.
   *
   * Both halves have to agree, and each covers the other's failure. The meter alone would hold
   * the turn open forever in a noisy room - hence the cap. The transcript alone is what was
   * wrong to begin with. If the analyser never started, voiceHearsSpeech stays false and this
   * degrades to exactly the old timer rather than to a turn that never ends.
   */
  private onVoiceEndTimer(): void {
    this.voiceEndTimerId = null;
    // The user may have stopped voice mode, or Nova may have resumed, in the window between the
    // last result and this firing.
    const interrupted = this.voiceState === "interrupted";
    if (this.voiceState !== "listening" && !interrupted) return;

    const spoken = this.composeDictatedText().trim();
    // Nothing said yet - there is no turn to hand over, and the browser's onend path is what
    // restarts a recognizer that has gone quiet. While Nova is paused the resume timer covers
    // this case instead, so falling through here is right in both states.
    if (!spoken) return;

    const waited = Date.now() - this.voiceLastResultAt;
    if (this.voiceHearsSpeech && waited < VOICE_END_MAX_WAIT_MS) {
      this.armVoiceEndTimer(VOICE_END_RECHECK_MS);
      return;
    }

    if (!interrupted) {
      this.submitVoiceTurn(spoken);
      return;
    }

    // She is paused mid-sentence and the user has finished. An acknowledgement was never a bid
    // for the turn, so she carries on from where she stopped; anything else takes the floor.
    if (this.voiceDebug) {
      // eslint-disable-next-line no-console
    }
    if (isBackchannel(spoken)) {
      this.resumeSpokenTurn();
      return;
    }

    this.commitInterruption(spoken);
  }

  private clearVoiceEndTimer(): void {
    if (this.voiceEndTimerId) clearTimeout(this.voiceEndTimerId);
    this.voiceEndTimerId = null;
  }

  private onDictationEnd(): void {
    // Commit before any restart: event.results starts empty again in the next session,
    // so anything not banked here would be dropped from the transcript.
    this.dictateCommitted = this.composeDictatedText();
    this.dictateSessionText = "";
    // FLIP-232. The prefix describes one session's results, and the next session starts from an
    // empty array - carrying it over would strip a matching run of the user's own words.
    this.dictateWarmupPrefix = "";

    if (this.dictateUserStopped || !this.isListening) {
      this.stopDictation();
      return;
    }

    // FLIP-158. In voice mode the browser's end-of-speech heuristic is a CANDIDATE turn
    // boundary: usually that silence is the user handing the turn over. In text dictation the
    // same event means "the user is mid-sentence, keep going", which is why this branches here
    // rather than changing the restart logic below - both behaviours are correct for their mode.
    if (this.voiceState === "listening") {
      const spoken = this.dictateCommitted.trim();
      // ...unless the microphone says otherwise. Chrome ends the session on its own heuristic,
      // which can land mid-sentence; submitting then is the same cut-off the timer used to
      // cause, so the recognizer is restarted below and the turn stays with the user.
      if (spoken && !this.voiceHearsSpeech) {
        this.submitVoiceTurn(spoken);
        return;
      }
      // Nothing said yet. Fall through and restart, so an end fired during the user's silence
      // doesn't quietly drop them out of the conversation.
    } else if (this.voiceState === "interrupted") {
      // Same boundary, but Nova is paused behind it, so the same two outcomes apply as in
      // onVoiceEndTimer rather than an unconditional send.
      const spoken = this.dictateCommitted.trim();
      if (spoken && !this.voiceHearsSpeech) {
        if (isBackchannel(spoken)) {
          this.resumeSpokenTurn();
        } else {
          this.commitInterruption(spoken);
        }
        return;
      }
      // Nothing said. The resume timer decides, and it needs a live recogniser until it does.
    }

    // The browser ended the session on its own end-of-speech heuristic. Restart, so the
    // stop button stays the only thing that actually ends dictation.
    const healthy =
      this.dictateSessionHadResult ||
      Date.now() - this.dictateSessionStartedAt >= DICTATE_MIN_HEALTHY_SESSION_MS;
    this.dictateFailedRestarts = healthy ? 0 : this.dictateFailedRestarts + 1;
    if (this.dictateFailedRestarts >= DICTATE_MAX_FAILED_RESTARTS) {
      this.stopDictation();
      return;
    }
    this.startRecognition();
  }

  private onDictationError(event: any): void {
    if (DICTATE_TERMINAL_ERRORS.indexOf(event.error) !== -1) {
      // eslint-disable-next-line no-console
      console.warn("[NovaCompanionChat] Speech recognition error:", event.error);
      this.stopDictation();
      return;
    }
    // no-speech / aborted / network are all recoverable - onend fires next and restarts us.
  }

  /**
   * FLIP-232. Removes the warm-up transcript - Nova's own tail - from a session transcript.
   *
   * `event.results` is CUMULATIVE for the life of a recognizer session, so once the handoff has
   * happened the user's words arrive appended to whatever the warm recognizer already had. A
   * result INDEX would be the obvious baseline and is the wrong one: the result straddling the
   * handoff can hold the last of her sentence and the first of theirs in a single string, and
   * dropping it wholesale would lose exactly the "okay" this is here to keep. Stripping the text
   * splits inside that result instead.
   *
   * If the recognizer revises its earlier guess the prefix stops matching, and the raw transcript
   * is used rather than throwing away words that might be the user's. That fails towards a stray
   * word of Nova's in the transcript instead of towards a lost turn - and the browser's echo
   * cancellation, plus a window this short, keeps it rare.
   */
  private stripWarmupPrefix(raw: string): string {
    if (!this.dictateWarmupPrefix) return raw;
    if (raw.indexOf(this.dictateWarmupPrefix) === 0) {
      return raw.slice(this.dictateWarmupPrefix.length);
    }
    return raw;
  }

  /** base text excluded: this is the dictated portion only, banked plus in-flight. */
  private composeDictatedText(): string {
    return [this.dictateCommitted, this.dictateSessionText]
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .join(" ");
  }

  stopDictation(): void {
    if (!this.isListening && !this.recognition) return;
    // Set before stop() so the onend this triggers doesn't restart the recognizer.
    this.dictateUserStopped = true;
    this.isListening = false;
    if (this.dictateIntervalId) clearInterval(this.dictateIntervalId);
    this.dictateIntervalId = null;
    if (this.recognition) {
      const rec = this.recognition;
      this.recognition = null;
      rec.onstart = null;
      rec.onresult = null;
      rec.onend = null;
      rec.onerror = null;
      try {
        rec.stop();
      } catch {
        // Already stopped - nothing to do.
      }
    }
    this.dictateBaseText = "";
    this.dictateCommitted = "";
    this.dictateSessionText = "";
    this.dictateFailedRestarts = 0;
    // FLIP-232. Both belong to the recognizer that has just been torn down.
    this.voiceWarmingRecognizer = false;
    this.dictateWarmupPrefix = "";
    // The transcript is deliberately left in inputText for the user to review and send.
    // Grow the composer to fit it - ngModel writes don't fire the (input) event the
    // textarea's auto-expand normally listens on.
    this.onInputResize();
  }

  // ── Voice mode (FLIP-158) ──
  //
  // A turn-taking loop over the three pieces that already existed rather than a second chat
  // stack: the browser recognizer above for listening, the SAME SendMessage endpoint text mode
  // uses for the reply, and api/Nova/Speak for the audio. Nothing about the system prompt,
  // guardrails, nav parsing or safety escalation is duplicated or bypassed - a voice turn walks
  // through sendUserMessage/handleReply exactly as a typed one does.
  //
  // Deliberately NOT the OpenAI Realtime API: that owns its own session, model and history, so
  // adopting it would mean maintaining Nova's guardrails in two places.

  /** True whenever the composer should be replaced by the voice status bar. */
  get isVoiceActive(): boolean {
    return this.voiceState !== "off";
  }

  /**
   * Status-bar copy, keyed to what is actually happening rather than to the state name.
   *
   * "speaking" is entered when the first SENTENCE of the reply exists, which is one
   * /api/Nova/Speak round-trip before any sound - so the bar claimed she was speaking through
   * several seconds of silence. And before that, "thinking" showed "Nova is listening", which
   * claimed the opposite. Both readings were wrong, in opposite directions, across the two halves
   * of the same wait: the user was told she was listening while she was thinking, then told she
   * was talking while she was silent.
   *
   * The honest split is on whether audio has actually started. It does not make the wait shorter,
   * but a wait you can see the shape of is a different experience from one that keeps telling you
   * it has already ended.
   */
  get voiceStatusLabel(): string {
    // FLIP-276. Same honest split as below, but on a fact rather than an inference: the transport
    // says when the audio actually started, so "Nova is speaking" cannot appear over silence.
    // FLIP-284. "Nova is thinking" is gone from this path. It flashed on every turn at ~700ms of
    // model latency, and it was not true: the microphone stays live the whole time under full
    // duplex, so she is listening at exactly the moment the bar claimed she was only thinking.
    // What remains is the one genuine wait - before the peer connection exists - which says so.
    // FLIP-486. Before the path is even chosen - the same honest "Connecting" the Realtime wait gets.
    if (this.voicePathPending) return "Connecting\u2026";
    if (this.realtimePathActive && !this.realtimeConnected) return "Connecting\u2026";
    if (this.realtimeVoice) {
      const view = this.realtimeVoice;
      // FLIP-276's instant barge-in acknowledgement, KEPT for a genuine one: `state` flips to
      // "interrupted" the instant server_vad reports ANY sound, and server_vad has no semantics -
      // Nova's own voice leaking back through an open mic (imperfect echo cancellation, no
      // headset), a cough, a backchannel, all count exactly as much as a real interruption does.
      // interrupt_response is on, so the server usually does end the reply - but not necessarily
      // at once, and audio already in the WebRTC pipe keeps playing regardless of what the label
      // says. Reporting "listening" on the bare VAD blip is how the bar used to go stale for the
      // rest of the turn while she kept audibly talking. So: "interrupted" is only trusted as
      // "she's stopped" when there was no audio THIS turn yet to begin with (realtimeAudioStartedAt
      // is 0) - a real cut takes the honest route below instead, exactly like a normal turn end.
      if (view.state === "listening") return "Nova is listening";
      if (view.state === "interrupted" && this.realtimeAudioStartedAt === 0) {
        return "Nova is listening";
      }
      // "Speaking" once this reply has actually produced audio, and for as long as the reply
      // lasts - not for as long as one audio buffer happens to be full, and not for less than
      // that just because a stray sound made the server think she was interrupted. A genuine end
      // (`audioBufferCleared` or `responseDone`) clears `responseActive` and sets `state` to
      // "listening" itself, so this still resolves to "listening" the moment she really has.
      return view.responseActive && this.realtimeAudioStartedAt > 0
        ? "Nova is speaking"
        : "Nova is listening";
    }
    if (this.voiceState === "listening" || this.voiceState === "interrupted") {
      return "Nova is listening";
    }
    // "speaking" with nothing played yet is still preparation, and says so.
    if (this.voiceState === "speaking" && this.spokenChunkIndex >= 0) return "Nova is speaking";
    return "Nova is thinking";
  }

  /** True while Nova is paused mid-sentence waiting to hear whether the user wants the turn. */
  get isVoiceInterrupted(): boolean {
    return this.voiceState === "interrupted";
  }

  /**
   * Header toggle copy. Deliberately only on/off - which side of the turn we're on is the
   * status bar's job, and duplicating it here would give the user two things to watch.
   */
  get voiceToggleLabel(): string {
    return this.isVoiceActive ? "Voice Chat Active" : "Voice Chat";
  }

  /**
   * Tracked by index, not by value: every bar's height changes every frame, so identity tracking
   * would tear down and rebuild sixty-odd DOM nodes twenty times a second.
   */
  trackVoiceBar(index: number): number {
    return index;
  }

  /**
   * The Voice Chat toggle. Turning Voice Chat on while N03B's prompt is up is the genuine use
   * (FLIP-391), which completes the lesson - the prompt itself is shown by the chat's lesson
   * sequence, not by this tap. Turning it off is never a lesson event.
   */
  onVoiceToggleSelected(): void {
    if (!this.isVoiceActive) {
      this.n03b.onTargetUsed("voice_chat");
    }
    this.toggleVoiceMode();
  }

  toggleVoiceMode(): void {
    if (this.isVoiceActive) {
      this.stopVoiceMode();
      return;
    }

    // FLIP-486. The server has not yet said which path to open - the first tap after an idle spell,
    // while the API is still waking. Say "Connecting" now and wait, rather than guessing legacy.
    if (this.realtimeEnabled === null) {
      this.resolveVoicePathThenToggle();
      return;
    }

    // FLIP-277. Which question this asks depends on which path will actually run. The Realtime
    // path speaks WebRTC and never touches Web Speech, so requiring SpeechRecognition there turns
    // away Safari and Firefox for no reason; the legacy path really does need the recogniser, so
    // its requirement stands until FLIP-279 removes that path. Dictation's gate is separate and
    // deliberately untouched - after this, "can this browser do voice chat" and "can this browser
    // do dictation" are different questions with different answers.
    //
    // FLIP-278. The flag asked is now the SERVER's, carried on GetChatContext. The build-time
    // environment flag this used to read is gone: it shipped false everywhere, so it would have
    // held every client on the legacy path no matter what the App Setting said, which is the one
    // thing the App Setting exists to prevent.
    const support = this.realtimeEnabled
      ? probeRealtimeVoiceSupport(window as any)
      : probeLegacyVoiceSupport(window as any);
    if (!support.supported) {
      // eslint-disable-next-line no-alert
      alert(describeMissingVoiceSupport(support.missing));
      return;
    }

    this.showChips = false;
    // Anything half-typed would otherwise be prepended to the first spoken turn, since
    // dictation appends to the field by design.
    this.inputText = "";
    // Per session, not per app load: if the back-end is fixed mid-conversation, the next session
    // should be able to report a fresh failure rather than staying quiet about a new one.
    this.voiceSpeechFailureNotified = false;

    this.voiceSessionMarks = { opened: Date.now(), listeningReady: 0 };
    this.voiceSessionTurnIndex = 0;

    if (this.realtimeEnabled) {
      this.startRealtimeVoiceMode();
      return;
    }

    // FLIP-230. Before resumeListening, so the warm-up request is in flight while the browser is
    // still asking for the microphone and starting the recogniser. Those are the seconds this is
    // trying to spend: the user cannot have said anything yet, so nothing is waiting on the
    // server, and by the time they finish their opening sentence the digest, the prompt cache and
    // the connections to OpenAI are already warm.
    //
    // FLIP-278. Legacy path only. A Realtime session is minted with the prompt already baked in
    // and never calls /Speak, so there is nothing here for it to prime.
    this.warmUpVoiceSession();

    this.resumeListening();
  }

  /**
   * FLIP-486. The one shared request for the server's Realtime flag. A failure leaves it unset
   * again, so the next tap asks afresh instead of replaying the failure it was handed before.
   */
  private requestRealtimeFlag(): Observable<boolean | null> {
    if (!this.realtimeFlag$) {
      this.realtimeFlag$ = this.chatService.getRealtimeEnabled().pipe(
        tap((enabled) => {
          if (enabled === null) {
            this.realtimeFlag$ = null;
          } else {
            this.realtimeEnabled = enabled;
          }
        }),
        shareReplay(1)
      );
    }
    return this.realtimeFlag$;
  }

  /**
   * FLIP-486. A voice tap that arrived before the server said which path to open: show
   * "Connecting…" at once, wait for the answer, then run the toggle as if it had been there.
   *
   * The audio element is primed HERE, inside the tap. connect() primes it too, but by the time the
   * answer lands we are no longer in the gesture, and iOS refuses a play() from outside one - the
   * call would connect with a live microphone and no way to hear Nova.
   *
   * No answer, or a null one, does NOT fall back to the legacy path: that is the silent switch this
   * exists to remove. It says so in the thread and the next tap tries again.
   */
  private resolveVoicePathThenToggle(): void {
    this.realtimeSession().primeAudioElement();
    this.voicePathPending = true;
    this.voiceState = "thinking";
    this.voicePathSubscription = this.requestRealtimeFlag()
      .pipe(timeout(NovaCompanionChatComponent.VOICE_PATH_WAIT_MS))
      .subscribe({
        next: (enabled) => this.onVoicePathResolved(enabled),
        error: () => this.onVoicePathResolved(null),
      });
  }

  private onVoicePathResolved(enabled: boolean | null): void {
    this.voicePathSubscription = null;
    this.voicePathPending = false;
    this.voiceState = "off";
    if (enabled === null) {
      this.realtimeFlag$ = null;
      this.pushVoiceNotice(
        "I couldn't reach Nova's voice just now. Tap the microphone to try again."
      );
      return;
    }
    this.toggleVoiceMode();
  }

  /**
   * FLIP-278. Opens voice mode on the Realtime path.
   *
   * The order matters. The relay starts BEFORE the session, because it records turns by
   * subscribing to the transport's events and a subscription added after the connection is up can
   * miss the opening exchange - an unrecorded first turn is exactly the failure FLIP-274 exists to
   * prevent. Both are stopped again on any outcome that is not `started`, so a failed mint cannot
   * leave a relay listening to a session that never existed.
   *
   * A refusal or an outage does NOT fall back to the legacy path. The flag chose Realtime; quietly
   * running a different engine would leave the user, and anyone reading a support ticket, unable
   * to tell which one they had just used. `refused` is the user's own concurrent session or hourly
   * allowance and says so; `unavailable` is generally Nova being broken rather than this one path.
   */
  private startRealtimeVoiceMode(): void {
    const relay = this.realtimeRelay();
    relay.start();
    this.startRealtimeVoiceUi();
    // Set before the mint rather than after it, so a stopVoiceMode() arriving while the mint is
    // still in flight - the user changing their mind, or closing the sheet - still tears the
    // transport down. The alternative strands the session it is about to open.
    this.realtimePathActive = true;
    // "thinking" rather than "listening": nothing can be heard until the peer connection is up,
    // and the status bar claiming otherwise is the exact dishonesty voiceStatusLabel documents.
    this.voiceState = "thinking";

    // The relay ends the session itself once a conversation is demonstrably going unrecorded; this
    // is how voice mode finds out, and it is subscribed from the start because the relay can give
    // up before the first view is ever published.
    this.realtimeSubscription?.add(
      relay.notice$.subscribe((message) => this.abandonRealtimeVoiceMode(message))
    );

    // FLIP-283. Spoken turns reach the thread the way typed ones do, so a voice conversation is
    // visible as text while it happens and is still there once the call ends. Subscribed on the
    // per-session bag, so it goes down with the call rather than outliving it.
    //
    // The set is cleared HERE, at the start of a session, for the reason its own comment gives.
    this.renderedRealtimeItems.clear();
    this.realtimeSubscription?.add(
      relay.turns$.subscribe((turn) => this.appendRelayedTurn(turn))
    );

    this.subscription.add(
      this.realtimeSession()
        .start()
        .subscribe({
          next: (result) => this.handleRealtimeStartResult(result),
          // The transport already reports its own failures through notice$; this is the case where
          // start() itself threw, which leaves nothing else to say it.
          error: () =>
            this.abandonRealtimeVoiceMode(
              "I couldn't start voice chat just now. Please try again in a moment."
            ),
        })
    );
  }

  /** What the mint came back with. Only `started` leaves voice mode open. */
  private handleRealtimeStartResult(result: NovaRealtimeStartResult): void {
    if (result.outcome === "started") {
      // FLIP-284. The peer connection exists from here, so the status bar may stop saying
      // "Connecting" and start reporting what the audio is doing.
      this.realtimeConnected = true;
      // Subscribed HERE rather than alongside the relay's, because the transport announces a
      // failed connection on notice$ AND reports it as `unavailable` in this result. Listening
      // from the start would tell the user the same bad news twice, in two different wordings.
      // From this point on the only thing notice$ carries is the session hitting its time limit,
      // which nothing else reports.
      this.realtimeSubscription?.add(
        this.realtimeSession().notice$.subscribe((message) =>
          this.abandonRealtimeVoiceMode(message)
        )
      );
      // The transport's own view$ drives the status bar from here; nothing else to do.
      return;
    }

    if (result.outcome === "cancelled") {
      // The user hung up before Nova finished connecting. stopVoiceMode() already ran - it is what
      // cancelled the connect - and telling someone who chose not to connect that Nova "couldn't"
      // is the wrong story. The transport has cleaned up its own side; there is nothing to say.
      return;
    }

    this.abandonRealtimeVoiceMode(
      result.outcome === "refused" ? result.refusal.message : result.message
    );
  }

  /**
   * Takes voice mode back down after a Realtime session failed to open, and says why.
   *
   * stopVoiceMode() does the teardown rather than a bespoke unwind here, so the concurrency slot,
   * the relay, the UI subscriptions and the audio graph are all released by the same code that
   * releases them on an ordinary hang-up - one path to get wrong instead of two.
   */
  private abandonRealtimeVoiceMode(message: string): void {
    this.stopVoiceMode();
    this.pushVoiceNotice(message);
  }

  /**
   * Puts something the voice path needs to say into the thread, as Nova.
   *
   * The same shape the streamed-reply failure uses, and deliberately not an alert(): by the time
   * one of these is written the sheet is open and the user is looking at it, and a message in the
   * thread is still there to re-read a moment later when a modal would have been dismissed.
   */
  private pushVoiceNotice(message: string): void {
    this.thread.push({ kind: "message", role: "assistant", text: message });
    this.scheduleScrollToBottom("smooth");
  }

  /**
   * FLIP-230. Asks the server to do the first turn's one-off work now.
   *
   * Fire-and-forget by design, and the response is used for nothing except the debug log. Voice
   * mode must not wait on this and must not care whether it worked: a failed warm-up costs the
   * user a faster first reply, which is exactly what they had before this existed.
   */
  private warmUpVoiceSession(): void {
    this.subscription.add(
      this.chatService.warmUp().subscribe((result) => {
        if (!this.voiceDebug) return;
        // eslint-disable-next-line no-console
      })
    );
  }

  /** Hands the turn back to the user: clears the transcript and restarts the recognizer. */
  private resumeListening(): void {
    this.clearVoiceResumeTimer();
    this.stopPlayback();
    this.voiceState = "listening";
    // Her turn is over rather than paused; nothing is waiting to be resumed.
    this.voiceSpeakingSince = 0;
    // toggleDictation reads the field to decide what to append to, so it has to be empty
    // before the next turn starts rather than after it ends.
    this.inputText = "";
    this.handOffToRecognizer();
    this.startVoiceWaveform();
  }

  /**
   * FLIP-232. Gives the floor to the recognizer, warm if maybePrewarmRecognizer got there first.
   *
   * The two callers - the end of Nova's turn, and a barge-in landing inside the warm window -
   * both used to cold-start here. The warm case must NOT go through toggleDictation: it toggles,
   * so on an already-running recognizer it would stop the very one that was started early.
   */
  private handOffToRecognizer(): void {
    if (this.voiceWarmingRecognizer) {
      this.adoptWarmRecognizer();
      return;
    }
    if (!this.isListening) this.toggleDictation();
  }

  /**
   * FLIP-232. Takes the warm recognizer over as the user's, and draws the line under Nova.
   *
   * Everything transcribed up to this instant was her, so it is dropped: the banked text goes,
   * and the in-flight text becomes the prefix stripped from every later result. What survives is
   * only what is said from here on - which is the point, since the recognizer is already
   * capturing and the first "okay" does not have to wait for a mic to come up.
   */
  private adoptWarmRecognizer(): void {
    this.voiceWarmingRecognizer = false;
    this.dictateWarmupPrefix = this.dictateSessionText;
    this.dictateCommitted = "";
    // She is what it heard, and the composer is the user's line. The transcript should not show
    // a word of it.
    this.dictateBaseText = "";
    this.inputText = "";
    if (this.voiceDebug && this.dictateWarmupPrefix) {
      // eslint-disable-next-line no-console
    }
  }

  /**
   * FLIP-232. Starts the recognizer during the tail of Nova's final chunk.
   *
   * Called from the playback frame loop, which already has the audio element, rather than from a
   * timer of its own: a blob-backed duration is Infinity until Chrome has seeked, so anything
   * scheduled up front would be scheduled against a number that did not exist yet.
   *
   * Only the LAST chunk qualifies. Mid-reply the queue still has sentences in it, and a recognizer
   * started there would sit warm through the rest of her turn hearing nothing but her.
   */
  private maybePrewarmRecognizer(audio: HTMLAudioElement): void {
    if (this.voiceState !== "speaking") return;
    if (this.voiceWarmingRecognizer || this.isListening) return;
    // More of her still to come - this is not the handover.
    if (!this.speechSynthesisComplete || this.speechQueue.length > 0) return;
    if (!isFinite(audio.duration) || audio.duration <= 0) return;

    const remainingMs = (audio.duration - audio.currentTime) * 1000;
    if (remainingMs > VOICE_RECOGNIZER_WARMUP_MS) return;

    // Back inside Angular only now that something is actually going to happen. The guards above
    // run on every frame of every reply and must stay free; entering the zone to decide "not yet"
    // sixty times a second would run change detection for the length of her turn.
    this.zone.run(() => {
      // Set BEFORE toggleDictation: its result handler reads the flag to decide whether to
      // publish, and a result arriving with the flag unset would land in the composer as though
      // the user had said it.
      this.voiceWarmingRecognizer = true;
      this.dictateWarmupPrefix = "";
      this.toggleDictation();
    });
  }

  /**
   * The session's audio graph. Created lazily, and only ever once - both the mic tap and Nova's
   * playback hang off the same context, and a second one would cost another hardware stream.
   */
  private ensureVoiceAudioContext(): AudioContext | null {
    if (this.voiceAudioContext) return this.voiceAudioContext;
    const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    this.voiceAudioContext = new Ctor();
    return this.voiceAudioContext;
  }

  /**
   * Opens a second, read-only tap on the microphone purely to measure loudness.
   *
   * SpeechRecognition gives no amplitude - only transcripts - so there is nothing to drive a
   * meter from without this. Two consumers of one input device is fine; the permission was
   * already granted for the recognizer, so this does not prompt again.
   *
   * Failure is non-fatal by design: if the analyser cannot start, voice mode still works, it just
   * shows no wave. Losing the meter must never cost the user the conversation.
   */
  private startVoiceWaveform(): void {
    // Already tapped: the microphone stays open for the whole session now, including while Nova
    // speaks, because that is the only way to hear someone talk over her. Just point the wave at
    // it again.
    if (this.voiceMicAnalyser) {
      this.startVoiceWave(this.voiceMicAnalyser);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) return;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        // The user may have left listening while the permission promise was in flight.
        if (this.voiceState !== "listening") {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        const context = this.ensureVoiceAudioContext();
        if (!context) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        const analyser = context.createAnalyser();
        analyser.fftSize = VOICE_WAVE_FFT_SIZE;
        analyser.smoothingTimeConstant = 0.5;
        const source = context.createMediaStreamSource(stream);
        source.connect(analyser);
        // Silent, and deliberately connected: see voiceMicSource. Gain of exactly zero, so the
        // microphone never reaches the speakers - the point is to keep the graph alive, not to
        // hear anything.
        const sink = context.createGain();
        sink.gain.value = 0;
        analyser.connect(sink);
        sink.connect(context.destination);

        this.voiceMicStream = stream;
        this.voiceMicSource = source;
        this.voiceMicSink = sink;
        this.voiceMicAnalyser = analyser;
        this.voiceMicTimeBuffer = new Uint8Array(analyser.fftSize);
        this.startVoiceWave(analyser);
        // Session-scoped, like the tap it reads. It checks the state itself, so it costs nothing
        // on the turns where nobody could be talking over anybody.
        this.startBargeInMonitor();
      })
      .catch(() => {
        // Permission refused, no input device, or the tab lost the mic. The recognizer reports
        // its own failures; this one is silent because the wave is not load-bearing.
      });
  }

  /**
   * The analyser Nova's speech plays through.
   *
   * Note the connect to `destination`: from the moment a media element is routed into the graph,
   * the graph is the only path to the speakers. An analyser left dangling would not just fail to
   * draw - it would mute her.
   */
  private ensureVoicePlaybackAnalyser(): AnalyserNode | null {
    if (this.voicePlaybackAnalyser) return this.voicePlaybackAnalyser;
    const context = this.ensureVoiceAudioContext();
    if (!context) return null;
    const analyser = context.createAnalyser();
    analyser.fftSize = VOICE_WAVE_FFT_SIZE;
    analyser.smoothingTimeConstant = 0.5;
    analyser.connect(context.destination);
    this.voicePlaybackAnalyser = analyser;
    // Read every frame alongside the microphone's: the gate is a comparison between the two, so
    // both have to be sampled from the same instant to mean anything.
    this.voicePlaybackTimeBuffer = new Uint8Array(analyser.fftSize);
    return analyser;
  }

  /**
   * Routes one chunk of Nova's speech through the playback analyser so the wave can be driven by
   * what she is actually saying rather than by a canned animation.
   *
   * Every failure path here leaves the element playing normally, unrouted: a meter is never worth
   * a silent Nova.
   */
  private pipeVoicePlayback(audio: HTMLAudioElement): void {
    const analyser = this.ensureVoicePlaybackAnalyser();
    const context = this.voiceAudioContext;
    if (!analyser || !context) return;

    // Checked BEFORE routing, not after: a suspended context that is handed an element swallows
    // it, so asking to be resumed and routing anyway would trade a missing wave for a mute Nova.
    // This chunk plays unrouted and the resume - which the page's user activation permits, since
    // voice mode starts from a click - lands in time for the next one.
    if (context.state !== "running") {
      context.resume().catch(() => {
        // Nothing further to try. Playback is untouched either way; only the meter is lost.
      });
      return;
    }

    try {
      // A given element can only ever have one source node, which is why this is per chunk -
      // each chunk is a fresh Audio().
      const source = context.createMediaElementSource(audio);
      source.connect(analyser);
      this.voicePlaybackSource = source;
    } catch {
      // Already routed, or the context refused the element. It still plays; it just isn't drawn.
    }
  }

  /**
   * Fits the bar count to the strip's measured width.
   *
   * The alternative - a fixed count stretched across whatever space there is - was rejected for
   * the same reason the dictate overlay rejects it: the modal runs 280px to 480px, so any one
   * count is either sparse at the top of that range or crowded at the bottom. Here the pitch is
   * what stays fixed and the count follows, so the wave reads identically at every width.
   */
  private setVoiceBarCount(width: number): void {
    if (width <= 0) return;
    const pitch = VOICE_WAVE_BAR_WIDTH + VOICE_WAVE_BAR_GAP;
    // + gap because the last bar has no gap after it.
    const fits = Math.floor((width + VOICE_WAVE_BAR_GAP) / pitch);
    const count = Math.max(
      VOICE_WAVE_MIN_BAR_COUNT,
      Math.min(VOICE_WAVE_MAX_BAR_COUNT, fits)
    );
    if (count === this.voiceLevels.length) return;

    this.voiceLevels = new Array(count).fill(VOICE_WAVE_MIN_HEIGHT);
    if (this.voiceWaveAnalyser) this.mapVoiceWaveBins(this.voiceWaveAnalyser);
    // No repaint from here: the render loop is already running at 20fps and will pick the new
    // count up on its next frame. Forcing change detection out of a ResizeObserver callback
    // during the view query that installed it is how ExpressionChanged errors start.
  }

  /** Spaces the bars logarithmically across VOICE_WAVE_MIN_HZ..MAX_HZ. See those constants. */
  private mapVoiceWaveBins(analyser: AnalyserNode): void {
    const binHz = analyser.context.sampleRate / analyser.fftSize;
    const topBin = analyser.frequencyBinCount - 1;
    const count = this.voiceLevels.length;
    const octaves = VOICE_WAVE_MAX_HZ / VOICE_WAVE_MIN_HZ;
    this.voiceWaveBins = Array.from({ length: count }, (_, bar) => {
      const position = count === 1 ? 0 : bar / (count - 1);
      const hz = VOICE_WAVE_MIN_HZ * Math.pow(octaves, position);
      return Math.min(topBin, hz / binHz);
    });
  }

  /** Points the render loop at one analyser and starts it. Safe to call while already running. */
  private startVoiceWave(analyser: AnalyserNode): void {
    this.voiceWaveAnalyser = analyser;
    this.voiceFrequencyBuffer = new Uint8Array(analyser.frequencyBinCount);
    this.voiceTimeBuffer = new Uint8Array(analyser.fftSize);
    this.mapVoiceWaveBins(analyser);
    this.voiceWaveLastFrameAt = 0;
    this.voiceLastSignalAt = 0;
    this.voiceHasSignal = false;
    this.voiceHearsSpeech = false;
    if (this.voiceWaveFrameId !== null) return;
    // Outside Angular: a per-frame callback inside it would schedule a full change-detection
    // pass 60 times a second for a decorative meter.
    this.zone.runOutsideAngular(() => this.renderVoiceWaveFrame());
  }

  /** Stops the render loop and puts the wave back to hidden-and-flat. */
  private stopVoiceWave(): void {
    if (this.voiceWaveFrameId !== null) cancelAnimationFrame(this.voiceWaveFrameId);
    this.voiceWaveFrameId = null;
    this.voiceWaveAnalyser = null;
    this.voiceFrequencyBuffer = null;
    this.voiceTimeBuffer = null;
    // Flattened at the measured count, not reset to a default one: the strip is the same width
    // next turn, and rebuilding the array at a different length would rebuild the DOM with it.
    this.voiceLevels = new Array(this.voiceLevels.length).fill(VOICE_WAVE_MIN_HEIGHT);
    // Cleared so the next turn starts hidden rather than inheriting the tail of the last one.
    this.voiceHasSignal = false;
    this.voiceHearsSpeech = false;
    this.voiceLastSignalAt = 0;
  }

  private renderVoiceWaveFrame(): void {
    this.voiceWaveFrameId = requestAnimationFrame(() => this.renderVoiceWaveFrame());

    const analyser = this.voiceWaveAnalyser;
    const frequencies = this.voiceFrequencyBuffer;
    const samples = this.voiceTimeBuffer;
    if (!analyser || !frequencies || !samples) return;

    const now = Date.now();
    if (now - this.voiceWaveLastFrameAt < VOICE_WAVE_FRAME_MS) return;
    this.voiceWaveLastFrameAt = now;

    // Two reads of the same signal, for two different questions. Is anyone talking is an
    // amplitude question, and only the time-domain samples answer it in units that have a zero -
    // see VOICE_WAVE_SPEECH_RMS. What the wave looks like is a spectral one.
    analyser.getByteTimeDomainData(samples as any);
    let sumOfSquares = 0;
    for (let i = 0; i < samples.length; i++) {
      const sample = ((samples[i] ?? 128) - 128) / 128;
      sumOfSquares += sample * sample;
    }
    const rms = Math.sqrt(sumOfSquares / samples.length);

    analyser.getByteFrequencyData(frequencies as any);

    // Read the spectrum where each bar sits, blending the two bins it falls between so a wave
    // with more bars than bins is a smooth curve rather than a staircase of repeated heights.
    const bins = this.voiceWaveBins;
    const next: number[] = [];
    for (let bar = 0; bar < bins.length; bar++) {
      const position = bins[bar] ?? 0;
      const lower = Math.floor(position);
      const upper = Math.min(frequencies.length - 1, lower + 1);
      const blend = position - lower;
      const level =
        ((frequencies[lower] ?? 0) * (1 - blend) + (frequencies[upper] ?? 0) * blend) / 255;
      const target = VOICE_WAVE_MIN_HEIGHT + level * (100 - VOICE_WAVE_MIN_HEIGHT);
      const previous = this.voiceLevels[bar] ?? VOICE_WAVE_MIN_HEIGHT;
      next.push(previous * VOICE_WAVE_SMOOTHING + target * (1 - VOICE_WAVE_SMOOTHING));
    }

    if (rms >= VOICE_WAVE_SPEECH_RMS) this.voiceLastSignalAt = now;
    const hadSignal = this.voiceHasSignal;
    this.voiceHasSignal = now - this.voiceLastSignalAt < VOICE_WAVE_HOLD_MS;
    this.voiceHearsSpeech = now - this.voiceLastSignalAt < VOICE_SPEECH_HOLD_MS;

    // Nothing to draw and nothing to hide: skip the repaint entirely rather than run change
    // detection 20 times a second over a silent room.
    if (!this.voiceHasSignal && !hadSignal) return;

    this.voiceLevels = next;
    // Repaints this component's subtree only - see the cdr note on the constructor.
    this.cdr.detectChanges();
    // And the minimised launcher, which is a different component tree entirely and so is NOT
    // covered by the detectChanges above. Gated on voiceDocked because that is the only time
    // anything is drawing this: while the sheet is open the circle is showing Nova's mascot.
    if (this.voiceDocked) this.publishVoiceMeter();
  }

  /**
   * Runs the barge-in check for the life of the session, independently of anything being drawn.
   *
   * setInterval rather than requestAnimationFrame: this has to keep running while Nova speaks and
   * the user is looking at something else, and rAF stops being delivered in a backgrounded tab -
   * which would silently make her uninterruptible exactly when someone turns away to think.
   */
  private startBargeInMonitor(): void {
    if (this.voiceBargeInMonitorId !== null) return;
    // Outside Angular for the same reason the wave is: 20 checks a second, each of which normally
    // decides that nothing has happened, must not each drag a change-detection pass with them.
    this.zone.runOutsideAngular(() => {
      this.voiceBargeInMonitorId = setInterval(
        () => this.checkForBargeIn(Date.now()),
        VOICE_WAVE_FRAME_MS
      );
    });
  }

  private stopBargeInMonitor(): void {
    if (this.voiceBargeInMonitorId !== null) clearInterval(this.voiceBargeInMonitorId);
    this.voiceBargeInMonitorId = null;
    this.voiceBargeInSince = 0;
    this.voiceBargeInLastLoudAt = 0;
  }

  /**
   * Measures Nova's current output, as RMS, for the gate to be referenced against.
   *
   * Zero when she is not routed through the analyser at all, which is the honest answer: an
   * unrouted chunk contributes no measurable echo reference, and the gate falls back to its
   * absolute floor rather than to a number that was never read.
   */
  private currentPlaybackRms(): number {
    const analyser = this.voicePlaybackAnalyser;
    const samples = this.voicePlaybackTimeBuffer;
    if (!analyser || !samples) return 0;
    analyser.getByteTimeDomainData(samples as any);
    let sumOfSquares = 0;
    for (let i = 0; i < samples.length; i++) {
      const sample = ((samples[i] ?? 128) - 128) / 128;
      sumOfSquares += sample * sample;
    }
    return Math.sqrt(sumOfSquares / samples.length);
  }

  /**
   * Pauses Nova if the user starts talking over her.
   *
   * Reads the MICROPHONE, not whichever analyser is currently driving the wave - during Nova's
   * turn that is her own playback, and asking it whether anyone is talking would only ever hear
   * her. The guards, in the order they reject:
   *
   *  - The grace period, measured from the end of the user's turn - see VOICE_BARGE_IN_GRACE_MS.
   *    Deliberately NOT suppressed while synthesis is still in flight: Nova being silent because
   *    she has not been rendered yet is the BEST moment to catch someone adding something, not a
   *    reason to ignore them. Talking there abandons the pending reply outright, which is right,
   *    because it is a reply the user has not heard one word of.
   *  - The level gate, referenced to her concurrent output - see VOICE_BARGE_IN_ECHO_RATIO.
   *  - A duration, so a cough does not stop her.
   *
   * What follows is a PAUSE, not the end of her turn. Whether the turn actually changes hands is
   * decided by what the user says, in onVoiceEndTimer - see pauseForUser.
   */
  private checkForBargeIn(now: number): void {
    if (this.voiceState !== "speaking") {
      this.voiceBargeInSince = 0;
      return;
    }

    const analyser = this.voiceMicAnalyser;
    const samples = this.voiceMicTimeBuffer;
    const debug = this.voiceDebug && now - this.voiceDebugLoggedAt > 250;
    if (debug) this.voiceDebugLoggedAt = now;

    // Logged BEFORE the early returns, so that "nothing printed" narrows to one thing: the monitor
    // is not reaching here at all.
    if (!analyser || !samples) {
      if (debug) {
        // eslint-disable-next-line no-console
      }
      return;
    }

    const inGrace = now - this.voiceSpeakingSince < VOICE_BARGE_IN_GRACE_MS;

    analyser.getByteTimeDomainData(samples as any);
    let sumOfSquares = 0;
    for (let i = 0; i < samples.length; i++) {
      const sample = ((samples[i] ?? 128) - 128) / 128;
      sumOfSquares += sample * sample;
    }
    const rms = Math.sqrt(sumOfSquares / samples.length);

    // Whichever is higher: the absolute floor, or a fixed fraction of what Nova is putting out
    // right now. In her gaps the second term collapses and this is just the floor.
    const playback = this.currentPlaybackRms();
    const floor = bargeInThreshold();
    const threshold = Math.max(floor, playback * bargeInEchoRatio());

    if (debug) {
      // eslint-disable-next-line no-console
    }

    if (inGrace) return;

    if (rms >= threshold) {
      if (this.voiceBargeInSince === 0) this.voiceBargeInSince = now;
      this.voiceBargeInLastLoudAt = now;
    } else if (
      this.voiceBargeInSince !== 0 &&
      now - this.voiceBargeInLastLoudAt > VOICE_BARGE_IN_HANGOVER_MS
    ) {
      // Quiet for longer than a gap inside speech, so whatever it was has stopped. Deliberately
      // NOT "the first quiet frame" - see VOICE_BARGE_IN_HANGOVER_MS.
      this.voiceBargeInSince = 0;
    }

    if (this.voiceBargeInSince === 0) return;
    if (now - this.voiceBargeInSince < VOICE_BARGE_IN_MS) return;

    this.voiceBargeInSince = 0;
    this.voiceBargeInLastLoudAt = 0;
    // Back inside Angular: this changes rendered state, and the monitor runs outside the zone.
    this.zone.run(() => this.pauseForUser());
  }

  /**
   * Stops Nova mid-sentence and listens, WITHOUT ending her turn.
   *
   * The distinction from resumeListening is the whole point: that one calls stopPlayback, which
   * bumps speechTurn and clears the queue, the chunk plans and the tokens - after it there is
   * nothing left to go back to. This pauses the audio element and leaves every one of those
   * standing, so resumeSpokenTurn can pick the sentence back up from where it stopped.
   */
  private pauseForUser(): void {
    if (this.voiceState !== "speaking") return;

    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
      } catch {
        // Already paused, or the element is mid-teardown. Either way it is not making sound.
      }
    }

    this.voiceState = "interrupted";
    this.voiceInterruptedAt = Date.now();

    // The wave belongs to the user again; her analyser is silent now anyway.
    if (this.voiceMicAnalyser) this.startVoiceWave(this.voiceMicAnalyser);

    this.inputText = "";
    // FLIP-232. A barge-in can land inside the warm window - she is still speaking, and the
    // recognizer for the turn after hers is already up. Adopting it here keeps that head start
    // and discards her tail; cold-starting a second one would throw both away.
    this.handOffToRecognizer();
    this.startVoiceWaveform();

    // If no words arrive at all, this was a cough and she should carry on.
    this.armVoiceResumeTimer();
  }

  private armVoiceResumeTimer(delay: number = VOICE_INTERRUPT_RESUME_MS): void {
    this.clearVoiceResumeTimer();
    this.voiceResumeTimerId = setTimeout(
      () => this.zone.run(() => this.resumeSpokenTurnIfSilent()),
      delay
    );
  }

  private clearVoiceResumeTimer(): void {
    if (this.voiceResumeTimerId) clearTimeout(this.voiceResumeTimerId);
    this.voiceResumeTimerId = null;
  }

  /**
   * The cough path: paused, nothing said, so carry on rather than wait for a turn nobody took.
   *
   * Both witnesses have to agree that nothing is happening, and the microphone is the one that
   * matters here. pauseForUser COLD-STARTS the recogniser, so "no transcript yet" is its normal
   * state for the first few hundred milliseconds of precisely the speech that caused the pause -
   * asking the transcript alone had Nova resume over the top of someone who had just started
   * talking, which is the opposite of what the pause is for.
   *
   * Capped, for the same reason onVoiceEndTimer is capped: the meter alone would hold her paused
   * indefinitely in a room that is simply noisy.
   */
  private resumeSpokenTurnIfSilent(): void {
    this.voiceResumeTimerId = null;
    if (this.voiceState !== "interrupted") return;

    // Words arrived. The turn belongs to the user now, and only end-of-utterance decides what
    // becomes of it - never this timer.
    if (this.composeDictatedText().trim()) return;

    const waited = Date.now() - this.voiceInterruptedAt;
    if (this.voiceHearsSpeech && waited < VOICE_INTERRUPT_MAX_WAIT_MS) {
      if (this.voiceDebug) {
        // eslint-disable-next-line no-console
      }
      this.armVoiceResumeTimer(VOICE_INTERRUPT_RECHECK_MS);
      return;
    }

    if (this.voiceDebug) {
      // eslint-disable-next-line no-console
    }
    this.resumeSpokenTurn();
  }

  /**
   * Picks Nova's sentence back up from where she was paused.
   *
   * Nothing is re-synthesised and nothing is re-fetched: the same audio element resumes at the
   * same offset, so the turn carries on where it was paused.
   */
  private resumeSpokenTurn(): void {
    this.clearVoiceResumeTimer();
    this.clearVoiceEndTimer();
    this.stopDictation();
    this.inputText = "";

    this.voiceState = "speaking";
    // Re-armed from NOW, not from her original start: the user has just been talking, and their
    // trailing words are in the room again exactly as they were the first time.
    this.voiceSpeakingSince = Date.now();
    this.voiceBargeInSince = 0;

    const analyser = this.ensureVoicePlaybackAnalyser();
    if (analyser) this.startVoiceWave(analyser);

    const audio = this.currentAudio;
    if (!audio) {
      // Paused between two chunks, so there is no element to resume - the queue is still intact,
      // and letting it pull the next one is the same continuation by another route.
      this.playNextIfIdle(this.speechTurn);
      return;
    }

    this.zone.runOutsideAngular(() => this.trackPlaybackProgress());
    audio.play().catch(() => {
      // The element would not restart. Rather than leave her frozen mid-turn, hand the floor back.
      this.zone.run(() => this.resumeListening());
    });
  }

  /**
   * The user meant it: end Nova's turn and send what they said.
   *
   * The reply she was part-way through is recorded as the part she actually SPOKE, not as the
   * whole thing. Nova's next answer is conditioned on that history, so leaving the full text in
   * would have her assume the user heard a paragraph they interrupted two sentences into - which
   * is how an interrupted assistant ends up repeating itself or referring back to something it
   * never said out loud.
   */
  private commitInterruption(text: string): void {
    this.clearVoiceResumeTimer();

    const spoken = this.spokenSoFar();
    // Invalidates the in-flight stream for this reply as well as its audio, so the rest of it
    // cannot land in the thread underneath the message that interrupted it.
    this.replyTurn++;
    this.removeTyping();
    this.recordInterruptedReply(spoken);

    this.stopPlayback();
    this.stopVoiceWave();
    this.stopDictation();
    this.inputText = "";
    this.voiceState = "thinking";
    this.sending = false;
    this.sendUserMessage(text);
  }

  /**
   * Breaks the wait between someone finishing and Nova starting into the parts it is made of.
   *
   * Three segments, because three different things can be slow and they have different owners:
   * deciding the turn ended (ours), the reply's first sentence arriving (the text model), and that
   * sentence coming back as audio (the speech model). Without the split, a slow turn is one
   * number that says only "it was slow".
   */
  private logTurnLatency(): void {
    if (!this.voiceDebug) return;
    const marks = this.voiceTurnMarks;
    if (!marks.finished) return;
    const now = Date.now();
    const ms = (from: number, to: number) => (from && to ? to - from : 0);
    // eslint-disable-next-line no-console

    // FLIP-230. The server's own split of the "reply(text)" segment above. Without it that segment
    // is one number covering a database, a prompt assembly and a model call, and the ticket's
    // question - which of the four candidates dominates - cannot be answered from the client at
    // all. Logged separately rather than merged in, so it is obvious which clock each came from.
    const server = marks.serverTimings;
    if (server) {
      // eslint-disable-next-line no-console
    }

    if (this.voiceSessionTurnIndex === 1 && this.voiceSessionMarks.listeningReady) {
      // eslint-disable-next-line no-console
    }
  }

  /**
   * Cuts the interrupted reply down to the part Nova actually said aloud.
   *
   * REWRITES the existing entry rather than adding one beside it. The reply is recorded as text
   * the moment the stream finishes, seconds before the audio gets there, so by the time anyone
   * interrupts it is normally already in the history in full - and appending a truncated copy left
   * BOTH, which tells the model the user heard the whole thing and then heard part of it again.
   *
   * Nothing spoken at all removes the entry outright. That is the case where the user started
   * talking while Nova was still being synthesised: she never said one word of it, and a reply
   * nobody heard has no business conditioning the next one.
   */
  private recordInterruptedReply(spoken: string): void {
    // The ellipsis is load-bearing: it is the only thing marking the reply as cut off rather than
    // as one Nova chose to end there.
    const cut = spoken ? spoken + "..." : "";

    if (this.voiceReplyRecorded) {
      const index = this.lastIndexOfAssistant();
      if (index !== -1) {
        if (cut) {
          this.conversation[index].content = cut;
        } else {
          this.conversation.splice(index, 1);
        }
      }
      const threadIndex = this.lastIndexOfAssistantThread();
      if (threadIndex !== -1) {
        if (cut) {
          this.thread[threadIndex].text = cut;
        } else {
          this.thread.splice(threadIndex, 1);
        }
      }
      return;
    }

    // The stream had not finished, so nothing has been recorded yet and this is the only entry
    // the interrupted reply will ever get.
    if (!cut) return;
    this.thread.push({ kind: "message", role: "assistant", text: cut });
    this.conversation.push({ role: "assistant", content: cut });
  }

  private lastIndexOfAssistant(): number {
    for (let i = this.conversation.length - 1; i >= 0; i--) {
      if (this.conversation[i].role === "assistant") return i;
    }
    return -1;
  }

  private lastIndexOfAssistantThread(): number {
    for (let i = this.thread.length - 1; i >= 0; i--) {
      const entry = this.thread[i];
      if (entry.kind === "message" && entry.role === "assistant") return i;
    }
    return -1;
  }

  /**
   * What Nova has actually said aloud this turn, at chunk granularity.
   *
   * Chunk granularity is deliberate: a sentence truncated mid-clause reads as damaged text in the
   * thread and in the history, and finer precision buys nothing the model can use.
   */
  private spokenSoFar(): string {
    if (this.spokenChunkIndex < 0) return "";
    return this.pendingSpokenChunks
      .slice(0, this.spokenChunkIndex + 1)
      .join(" ")
      .trim();
  }

  /**
   * Tells the user once per voice session that synthesis is failing.
   *
   * A divider rather than an assistant bubble: this is the app reporting its own state, and
   * putting it in Nova's voice would have her announce she cannot speak while not speaking it.
   */
  private notifyVoiceUnavailable(): void {
    if (this.voiceSpeechFailureNotified) return;
    this.voiceSpeechFailureNotified = true;
    this.thread.push({
      kind: "divider",
      text: "Nova's voice isn't available right now - her replies will appear as text.",
    });
    if (this.shouldFollowThread()) this.scheduleScrollToBottom("smooth");
  }

  /**
   * Releases the microphone tap. Called on every exit from listening, not just on teardown.
   *
   * The context outlives this: Nova's turn is next, and it is where her audio is analysed.
   */
  private stopVoiceWaveform(): void {
    this.stopVoiceWave();
    this.stopBargeInMonitor();
    this.voiceMicTimeBuffer = null;
    // Tracks must be stopped explicitly or the browser keeps showing the tab as recording long
    // after voice mode ends.
    this.voiceMicStream?.getTracks().forEach((track) => track.stop());
    this.voiceMicStream = null;
    this.voiceMicSource?.disconnect();
    this.voiceMicSource = null;
    this.voiceMicSink?.disconnect();
    this.voiceMicSink = null;
    this.voiceMicAnalyser = null;
  }

  /** Tears down the whole audio graph. Only at the end of a session - see stopVoiceWaveform. */
  private closeVoiceAudio(): void {
    this.voicePlaybackAnalyser = null;
    this.voicePlaybackTimeBuffer = null;
    this.voiceSessionMarks = { opened: 0, listeningReady: 0 };
    this.voiceSessionTurnIndex = 0;

    if (this.voiceAudioContext) {
      this.voiceAudioContext.close().catch(() => {
        // Already closed, or closing during teardown - nothing depends on this resolving.
      });
    }
    this.voiceAudioContext = null;
  }

  /**
   * Ends the user's turn and sends what they said.
   *
   * This is the auto-send that plain dictation deliberately does not do (see the class comment):
   * in voice mode there is no keyboard in the loop to press send with, so the silence has to
   * mean send. Text dictation is untouched - it still parks the transcript in the field.
   */
  private submitVoiceTurn(text: string): void {
    // Both paths into here (the silence timer and the browser's own onend) can be pending at
    // once, and stopDictation below can trigger the other. Clearing first makes the second
    // arrival a no-op rather than a duplicate send.
    this.clearVoiceEndTimer();
    // The WAVE stops; the microphone does not. It stays open through Nova's turn so it can hear
    // the user talk over her - see the barge-in check in renderVoiceWaveFrame.
    this.stopVoiceWave();
    this.stopDictation();
    this.inputText = "";
    this.voiceTurnMarks = {
      finished: Date.now(),
      firstChunk: 0,
      synthSent: 0,
      serverTimings: null,
    };
    this.voiceSessionTurnIndex++;
    this.voiceState = "thinking";
    // The floor has just passed to Nova, and the grace period runs from here rather than from her
    // first spoken word - see VOICE_BARGE_IN_GRACE_MS. It is the user's trailing speech this
    // protects against, so it is the user's stopping that it is measured from.
    this.voiceSpeakingSince = Date.now();
    this.sendUserMessage(text);
  }

  /**
   * Opens a spoken turn. Called when the FIRST sentence of the reply is ready, not when the whole
   * reply is - which is the entire point of the streaming path.
   */
  private beginSpokenTurn(): void {
    // Before any state is set: stopPlayback clears it, so setting up first would immediately
    // undo the setup.
    this.stopPlayback();
    this.voiceState = "speaking";
    // Normally already set, by submitVoiceTurn, when the floor passed to her. This covers the
    // replies that do not come from a spoken turn at all - a typed message sent while voice mode
    // is on - which would otherwise inherit the previous turn's anchor or none.
    if (this.voiceSpeakingSince === 0) this.voiceSpeakingSince = Date.now();
    this.voiceBargeInSince = 0;
    // Her turn is metered the same way the user's is - the bars answer "is there sound", not
    // "whose turn is it", so the loop starts here and each chunk plays through the analyser.
    const analyser = this.ensureVoicePlaybackAnalyser();
    if (analyser) this.startVoiceWave(analyser);
    this.speechQueue = [];
    this.speechSynthesisComplete = false;
    this.speechStreamComplete = false;
    this.pendingSpokenChunks = [];
    this.parkedChunkIndex = null;
    this.spokenChunkIndex = -1;
    this.speechTurn++;
  }

  /**
   * Adds one finished sentence to the turn, and restarts synthesis if it had run out of work.
   *
   * Appended rather than computed from the whole reply, because the whole reply does not exist
   * yet - that is the point of streaming, and the reason sentence one can be synthesised while
   * sentence three is still being written.
   */
  private queueSpokenChunk(chunk: string): void {
    // Nothing to say, and an empty chunk would still advance the queue cursor spokenSoFar reads.
    if (!chunk.trim()) return;

    this.pendingSpokenChunks.push(chunk);

    const parked = this.parkedChunkIndex;
    if (parked !== null) {
      this.parkedChunkIndex = null;
      this.synthesizeFrom(parked, this.speechTurn);
    }
  }

  /**
   * Marks the reply finished. Until this, running out of sentences means "not written yet"; after
   * it, it means the turn is over.
   */
  private finishSpokenTurn(): void {
    this.speechStreamComplete = true;
    if (this.parkedChunkIndex !== null) {
      const parked = this.parkedChunkIndex;
      this.parkedChunkIndex = null;
      this.synthesizeFrom(parked, this.speechTurn);
    }
  }

  /**
   * Synthesises one chunk, queues it, and immediately starts the next. The next call goes out
   * while the current chunk is playing, so after the first chunk the audio should be ready
   * before the previous one finishes.
   */
  private synthesizeFrom(index: number, turn: number): void {
    if (turn !== this.speechTurn) return;

    if (index >= this.pendingSpokenChunks.length) {
      // Out of sentences. Whether that is the end of the turn depends on whether the reply has
      // finished arriving - if it has not, park here and wait to be restarted by the next one.
      if (!this.speechStreamComplete) {
        this.parkedChunkIndex = index;
        return;
      }
      this.speechSynthesisComplete = true;
      // The queue may already have drained while the last chunk was in flight, in which case
      // nothing else is going to notice the turn is over.
      this.finishSpeakingIfDone(turn);
      return;
    }

    if (this.voiceTurnMarks.synthSent === 0) this.voiceTurnMarks.synthSent = Date.now();
    this.subscription.add(
      this.chatService.speak(this.pendingSpokenChunks[index]).subscribe({
        next: (audio) => {
          if (turn !== this.speechTurn) return;
          this.speechQueue.push(audio);
          this.playNextIfIdle(turn);
          this.synthesizeFrom(index + 1, turn);
        },
        error: (error: unknown) => {
          if (turn !== this.speechTurn) return;
          // eslint-disable-next-line no-console
          console.warn(
            "[NovaCompanionChat] Speech synthesis failed; ending the spoken turn.",
            error
          );
          // Say so, once. A silent failure is indistinguishable from Nova choosing not to speak,
          // which is how an API returning 404 for every synthesis call came across as a design
          // decision rather than a back-end that needed restarting.
          this.notifyVoiceUnavailable();
          // The reply is already on screen as text, so the user has not lost the content -
          // stopping here beats a turn that never hands control back.
          this.speechSynthesisComplete = true;
          this.finishSpeakingIfDone(turn);
        },
      })
    );
  }

  private playNextIfIdle(turn: number): void {
    if (turn !== this.speechTurn) return;
    if (this.currentAudio) return;

    const next = this.speechQueue.shift();
    if (!next) {
      this.finishSpeakingIfDone(turn);
      return;
    }

    const url = URL.createObjectURL(next);
    const audio = new Audio(url);
    this.pipeVoicePlayback(audio);
    this.currentAudio = audio;
    this.currentAudioUrl = url;
    // The queue is strictly FIFO in chunk order, so counting plays is enough to know how much of
    // the reply has been said - the blobs themselves carry no identity.
    this.spokenChunkIndex++;
    if (this.spokenChunkIndex === 0) this.logTurnLatency();
    // Outside Angular for the same reason as the waveform: this runs every frame.
    this.zone.runOutsideAngular(() => this.trackPlaybackProgress());

    const onDone = () =>
      this.zone.run(() => {
        this.releaseCurrentAudio();
        if (turn !== this.speechTurn) return;
        this.playNextIfIdle(turn);
      });

    // HTMLAudioElement events aren't zone-patched here for the same reason SpeechRecognition
    // isn't - assigned as on* properties rather than addEventListener.
    audio.onended = onDone;
    audio.onerror = onDone;

    audio.play().catch(() => {
      // Autoplay was refused, or the blob wouldn't decode. Either way this chunk is lost;
      // move on rather than stalling the turn.
      this.zone.run(() => {
        this.releaseCurrentAudio();
        if (turn !== this.speechTurn) return;
        this.playNextIfIdle(turn);
      });
    });
  }

  /**
   * Drives the one thing the playback frame loop is still for: the recognizer's head start.
   *
   * FLIP-232's warm-up has to be decided against the AUDIO - a blob-backed duration is Infinity
   * until Chrome has seeked, so nothing useful can be scheduled up front - and a frame loop is
   * the only place that fact is continuously available. It carried the caption typewriter too
   * until captions were removed from voice mode; what is left is the prewarm check.
   *
   * Runs outside Angular. Nothing here touches the view, so no change detection is needed.
   */
  private trackPlaybackProgress(): void {
    this.spokenFrameId = requestAnimationFrame(() => this.trackPlaybackProgress());
    const audio = this.currentAudio;
    if (audio) this.maybePrewarmRecognizer(audio);
  }

  private stopSpokenWordTracking(): void {
    if (this.spokenFrameId !== null) cancelAnimationFrame(this.spokenFrameId);
    this.spokenFrameId = null;
  }

  /** Back to listening once every chunk has been synthesised AND played. */
  private finishSpeakingIfDone(turn: number): void {
    if (turn !== this.speechTurn) return;
    if (!this.speechSynthesisComplete) return;
    if (this.currentAudio || this.speechQueue.length > 0) return;
    if (this.voiceState !== "speaking") return;
    this.resumeListening();
  }

  private releaseCurrentAudio(): void {
    // Per chunk, not per reply: the next chunk starts its own loop against its own plan.
    this.stopSpokenWordTracking();
    // A source node pins the element it reads for the life of the context, so a long reply would
    // otherwise leave one paused <audio> per sentence hanging off the graph.
    this.voicePlaybackSource?.disconnect();
    this.voicePlaybackSource = null;
    if (this.currentAudio) {
      this.currentAudio.onended = null;
      this.currentAudio.onerror = null;
      try {
        this.currentAudio.pause();
      } catch {
        // Already stopped - nothing to do.
      }
    }
    this.currentAudio = null;
    if (this.currentAudioUrl) {
      // Object URLs pin the blob in memory until revoked, and a long voice session creates one
      // per sentence.
      URL.revokeObjectURL(this.currentAudioUrl);
      this.currentAudioUrl = null;
    }
  }

  private stopPlayback(): void {
    // Invalidates every in-flight synthesis and playback callback for the previous turn.
    this.speechTurn++;
    // Whatever the wave was drawing has stopped making sound. resumeListening restarts it
    // against the microphone once the mic tap is back.
    this.stopVoiceWave();
    this.speechQueue = [];
    this.speechSynthesisComplete = false;
    this.releaseCurrentAudio();
    this.spokenChunkIndex = -1;
    // The streamed half of the same state. Left behind, these would have the next turn's
    // first sentence land against the last turn's queue.
    this.pendingSpokenChunks = [];
    this.parkedChunkIndex = null;
    this.speechStreamComplete = false;
    this.queuedChunkCount = 0;
  }

  // ── Docked voice bar (FLIP-234) ──
  //
  // "Minimize" here collapses the sheet and leaves the dialog OPEN, rather than closing it and
  // reopening later. That is the whole mechanism: the microphone, the AudioContext, the
  // analysers, the recognizer, the playback queue and the conversation are all fields on this
  // component, so the session lasts exactly as long as the component does. Keep it mounted and
  // every acceptance criterion follows - including "restoring returns to the same conversation",
  // which is not implemented anywhere because nothing was ever thrown away.
  //
  // What docking has to undo is everything the dialog does to the page BEHIND it, since the user
  // is about to go and use it: the backdrop (CSS), the scroll block (the strategy passed through
  // data), and the focus trap (below). Route changes are handled at open time by
  // closeOnNavigation: false - see novaCompanionChatDialogConfig.

  /**
   * Collapse the sheet to the minimised launcher, leaving the call running.
   *
   * Gated on isVoiceActive as well as on the button's own *ngIf: "minimise is offered in voice
   * mode only, not in text chat" is a requirement, and a gate that lives only in the template
   * holds only for callers that go through the template.
   */
  minimizeVoice(): void {
    if (this.voiceDocked || !this.isVoiceActive) return;
    this.voiceDocked = true;
    this.dialogRef.addPanelClass(DOCKED_PANEL_CLASS);
    // Escape would otherwise close the dialog outright and end the call with it - a keystroke
    // away from losing the session, on a state whose entire point is that it survives. The
    // circle carries the way back explicitly, so nothing is trapped by taking Escape away.
    // Backdrop clicks are already moot: the backdrop is pointer-events:none while docked.
    //
    // Restored to whatever the caller opened with rather than hardcoded back to false, so this
    // stays a temporary suspension of their setting rather than an overwrite of it.
    this.closeDisabledBeforeDock = this.dialogRef.disableClose ?? false;
    this.dialogRef.disableClose = true;
    this.dialogData?.scrollStrategy.disable();
    this.setFocusTrapEnabled(false);
    this.launcherState.setVoiceDocked(true);
    // Ahead of the first wave frame, so the circle paints in the right colour with the right label
    // the instant it appears rather than a beat later. Focus is the circle's own job - it is in
    // another component, and it is the emission above that renders it.
    this.publishVoiceMeter();
  }

  /** Expand the launcher back into the full sheet, same conversation, same call. */
  restoreVoice(): void {
    if (!this.voiceDocked) return;
    this.undock();
    setTimeout(() => this.voiceMinimizeRef?.nativeElement.focus());
  }

  /**
   * Put back everything minimizeVoice() switched off. Safe to call when not docked.
   *
   * Called from restoreVoice and from stopVoiceMode, which is what guarantees the minimised
   * launcher never outlives the session it describes.
   */
  private undock(): void {
    if (!this.voiceDocked) return;
    this.voiceDocked = false;
    this.dialogRef.removePanelClass(DOCKED_PANEL_CLASS);
    this.dialogRef.disableClose = this.closeDisabledBeforeDock;
    // Re-blocks at the CURRENT scroll position rather than the one captured when the dialog
    // opened, so the restore-on-close that BlockScrollStrategy performs puts the user back where
    // they actually are.
    this.dialogData?.scrollStrategy.enable();
    this.setFocusTrapEnabled(true);
    this.launcherState.setVoiceDocked(false);
    // Cleared rather than left to go stale. The FAB stops drawing the wave on the flag above, so
    // this is not what hides it - but the meter is a BehaviorSubject, and a leftover value would
    // be replayed to the next subscriber as though a call were still running.
    this.launcherState.publishVoiceMeter(null);
  }

  /**
   * Hand NovaCompanionFabComponent everything its circle needs to draw itself.
   *
   * Called from the wave loop (20fps, outside the Angular zone), from every voiceState transition,
   * and once as the sheet collapses. Cheap by construction: the only work is bucketing the levels
   * down to the circle's bar count.
   *
   * Silent unless docked, so an open sheet publishes nothing - the FAB is showing Nova's mascot
   * then, and has unsubscribed from the meter anyway.
   */
  private publishVoiceMeter(): void {
    if (!this.voiceDocked) return;
    this.launcherState.publishVoiceMeter({
      // The same test the in-sheet status bar's gradient uses, so the two agree at every instant.
      speaking: this.voiceState === "speaking",
      hasSignal: this.voiceHasSignal,
      levels: compactVoiceLevels(this.voiceLevels, NOVA_VOICE_METER_BARS),
      label: this.voiceStatusLabel,
    });
  }

  /**
   * Turn the dialog's focus trap off while docked, on again when restored.
   *
   * Without this a keyboard user tabbing off the end of the bar wraps straight back into it -
   * which directly contradicts the requirement that they can navigate to any section of the app
   * while minimized. A mouse user never notices; a keyboard user cannot leave.
   *
   * Reached through _containerInstance because MatDialogConfig exposes autoFocus and
   * restoreFocus but nothing that turns the trap itself off, and there is no public handle on
   * the container's FocusTrap. Guarded on both hops and on the property actually existing, so a
   * Material upgrade that renames either degrades to "focus stays in the bar" rather than
   * throwing in the middle of minimizing.
   */
  private setFocusTrapEnabled(enabled: boolean): void {
    // Through unknown because _focusTrap is declared private on the container - the compiler is
    // right that the two types do not overlap, and that is exactly the reach being made here.
    const container = this.dialogRef?._containerInstance as unknown as
      | { _focusTrap?: { enabled: boolean } }
      | undefined;
    const trap = container?._focusTrap;
    if (trap && typeof trap.enabled === "boolean") trap.enabled = enabled;
  }

  // ---------------------------------------------------------------------------------------------
  // FLIP-276. The Realtime path's voice UI.
  //
  // FLIP-278 now calls this seam: startRealtimeVoiceMode() drives these two, and only when the
  // server's realtimeEnabled flag selected this path. Exactly one engine runs per call - the
  // legacy acoustic one is still reached, unchanged, whenever the flag is off, which is what makes
  // turning the App Setting back off a real recovery rather than a nominal one. FLIP-279 removes
  // the other engine once Realtime has passed validation.
  // ---------------------------------------------------------------------------------------------

  /**
   * Puts the voice UI on the Realtime path: state and waveform driven by the session rather than
   * by measured loudness.
   *
   * The transport is already running by the time this is called - it is `providedIn: "root"` and
   * outlives this component (FLIP-273) - so this subscribes to what it is already saying rather
   * than starting anything.
   */
  /**
   * The Realtime voice UI service, resolved on first use.
   *
   * Deliberately NOT a constructor dependency. It reaches the transport, which needs an HTTP
   * client, the signed-in user and the API base URL - so injecting it here would make every
   * component that constructs this one, and every spec in both suites, provide the whole Realtime
   * stack in order to render a chat sheet that may never open voice at all. Resolving it at the
   * point the Realtime path actually opens keeps that cost where the feature is.
   */
  private realtimeVoiceUi(): NovaRealtimeVoiceUiService {
    return (this.realtimeVoiceUiService ??= this.injector.get(NovaRealtimeVoiceUiService));
  }

  /**
   * FLIP-278. The transport and the relay, resolved the same way and for the same reason.
   *
   * Reached only from the Realtime branch, so a client on the legacy path - which is every client
   * until the App Setting is flipped - never constructs either, and no spec that renders this
   * component has to provide them.
   */
  private realtimeSession(): NovaRealtimeSessionService {
    return (this.realtimeSessionService ??= this.injector.get(NovaRealtimeSessionService));
  }

  private realtimeRelay(): NovaRealtimeRelayService {
    return (this.realtimeRelayService ??= this.injector.get(NovaRealtimeRelayService));
  }

  /**
   * Renders one relayed turn, once.
   *
   * DEDUPED ON ITEM ID, AND THE FIRST ONE WINS. `turns$` publishes on `recorded` OR `duplicate`,
   * so a barge-in publishes the same item TWICE: first the `conversation.item.truncate` that wrote
   * what was actually heard, then the `response.done` for the same item carrying the full reply,
   * which the server refused by the id the two share. Keeping the first is what makes the thread
   * agree with stored history — keeping the last would put the words the user interrupted back on
   * screen, which is the C7 divergence this migration exists to not inherit.
   *
   * Scrubbed on the way in because `ChatMessage.content` is documented as display text with tags
   * already stripped, and a relayed transcript has had nothing done to it. Until the voice prompt
   * stops teaching the tag contract (C1 / FLIP-281) the model can still emit one, and an unscrubbed
   * push would put `[[NAV:Score]]` in the scrollback.
   */
  private appendRelayedTurn(turn: NovaRelayedTurn): void {
    if (this.renderedRealtimeItems.has(turn.itemId)) return;

    const text = scrubCaption(turn.text ?? "").trim();
    // An empty turn is one the server declined to store, so there is nothing to agree with.
    // Recorded as seen regardless: a later duplicate of it is just as empty.
    this.renderedRealtimeItems.add(turn.itemId);
    if (!text) return;

    this.thread.push({ kind: "message", role: turn.role, text });
    this.conversation.push({ role: turn.role, content: text });
    // Coalesced, so a user turn and Nova's reply landing together scroll once.
    this.scheduleScrollToBottom();
  }

  /**
   * FLIP-284. Tracks when Nova's audio for the current reply began, or 0 while she is silent.
   *
   * The clock starts at the first audio of a reply and is NOT restarted by later chunks -
   * `output_audio_buffer.stopped` fires between them, so rewinding on each one would have the
   * status bar flicker back to "listening" mid-sentence. Read by voiceStatusLabel, which is the
   * only thing that still needs to know whether she is actually making sound.
   */
  private syncRealtimeAudioClock(
    previous: NovaRealtimeVoiceView | null,
    view: NovaRealtimeVoiceView
  ): void {
    // A new reply: no clock until the first audio of THIS response.
    if (view.responseActive && !previous?.responseActive) {
      this.realtimeAudioStartedAt = 0;
    }

    if (view.responseActive && view.audioStarted && this.realtimeAudioStartedAt === 0) {
      this.realtimeAudioStartedAt = Date.now();
    }

    if (!view.responseActive) {
      this.realtimeAudioStartedAt = 0;
    }
  }

  startRealtimeVoiceUi(): void {
    if (this.realtimeSubscription) return;

    const realtime = this.realtimeVoiceUi();

    const subscription = new Subscription();
    subscription.add(
      realtime.view$.subscribe((view) => this.applyRealtimeVoiceView(view))
    );
    subscription.add(
      realtime.remoteStream$.subscribe((stream) => {
        this.realtimeNovaStream = stream;
        this.updateRealtimeWaveSource();
      })
    );
    subscription.add(
      realtime.microphoneStream$.subscribe((stream) => {
        this.realtimeMicStream = stream;
        this.updateRealtimeWaveSource();
      })
    );
    this.realtimeSubscription = subscription;
  }

  /** Takes the voice UI back off the Realtime path and releases what it held. */
  stopRealtimeVoiceUi(): void {
    this.realtimeSubscription?.unsubscribe();
    this.realtimeSubscription = null;
    this.realtimeConnected = false;
    this.realtimeAudioStartedAt = 0;
    this.realtimeVoice = null;
    this.realtimeNovaStream = null;
    this.realtimeMicStream = null;
    this.stopVoiceWave();
    // The nodes go with the context, which stopVoiceMode closes. Dropping the references here is
    // what stops a second call reusing an analyser whose graph has already been torn down - an
    // analyser with nothing behind it reads zeros for ever, and looks exactly like silence.
    this.realtimeAnalysers.clear();
  }

  /** Renders one view from the Realtime session. */
  private applyRealtimeVoiceView(view: NovaRealtimeVoiceView): void {
    const previous = this.realtimeVoice;
    this.realtimeVoice = view;
    this.voiceState = view.state;

    this.syncRealtimeAudioClock(previous, view);
    this.updateRealtimeWaveSource();
  }

  /**
   * Points the wave at whoever currently has the floor.
   *
   * Nova's own stream while she is audibly speaking, the microphone the rest of the time. Both are
   * the streams the call is actually using, so the wave cannot disagree with what is being heard -
   * which a second, independently opened microphone tap can.
   */
  private updateRealtimeWaveSource(): void {
    const view = this.realtimeVoice;
    if (!view) return;

    const speaking = view.state === "speaking" && view.audioStarted;
    const stream = speaking ? this.realtimeNovaStream : this.realtimeMicStream;
    if (!stream) {
      this.stopVoiceWave();
      return;
    }

    const analyser = this.realtimeAnalyserFor(stream);
    // Restarting the loop on an analyser it is already reading would reset the wave's hold and
    // make it stutter on every view update, which is several times a sentence.
    if (analyser && analyser !== this.voiceWaveAnalyser) this.startVoiceWave(analyser);
  }

  /**
   * An analyser on one stream, built once and kept.
   *
   * Failure is non-fatal, exactly as it is on the legacy path: no AudioContext means no wave, and
   * losing the meter must never cost the user the conversation.
   */
  private realtimeAnalyserFor(stream: MediaStream): AnalyserNode | null {
    const existing = this.realtimeAnalysers.get(stream);
    if (existing) return existing;

    const context = this.ensureVoiceAudioContext();
    if (!context) return null;

    const analyser = context.createAnalyser();
    analyser.fftSize = VOICE_WAVE_FFT_SIZE;
    analyser.smoothingTimeConstant = 0.5;
    context.createMediaStreamSource(stream).connect(analyser);
    // Silent, and deliberately connected, for the reason the legacy mic tap documents: a graph
    // with no path to the destination becomes eligible for collection, and the analyser hanging
    // off it then reads zeros for ever without ever reporting an error.
    const sink = context.createGain();
    sink.gain.value = 0;
    analyser.connect(sink);
    sink.connect(context.destination);

    this.realtimeAnalysers.set(stream, analyser);
    return analyser;
  }

  stopVoiceMode(): void {
    // Captured before the state is torn down. This also runs on close and on destroy, when voice
    // may never have been on - and there is no thread to reveal, or reader to move, in that case.
    const wasActive = this.isVoiceActive;
    // FLIP-486. A hang-up while still waiting on the path answer: stop waiting, or the answer
    // landing later would start a call the user already walked away from.
    this.voicePathSubscription?.unsubscribe();
    this.voicePathSubscription = null;
    this.voicePathPending = false;
    // Unconditional, and before the teardown below: the bar has nothing to say once there is no
    // call. Every path that ends voice comes through here - the header toggle, the in-sheet stop
    // button, a revoked microphone, a terminal recognizer error - so this is the single place
    // that guarantees a docked bar can never be left claiming Nova is listening.
    //
    // The sheet reappearing over whatever page the user is now on is deliberate, and is only
    // reachable from a failure: the bar's own end control calls close(), which dismisses Nova
    // outright rather than expanding her. So the common path never pops the sheet, and the
    // uncommon one - voice stopping on its own - surfaces the thread, which is where whatever
    // went wrong is explained.
    this.undock();
    this.stopPlayback();
    this.clearVoiceEndTimer();
    this.clearVoiceResumeTimer();
    this.stopVoiceWaveform();
    // FLIP-278. Before the UI is unhooked, and unconditional once the Realtime path was the one
    // running: end() hands the concurrency slot back, and a slot that is never handed back locks
    // the user out of voice with `concurrent_session` until the window rolls over. Safe on a
    // session that has already closed itself - end() on an idle transport tears down what is left
    // and returns null.
    if (this.realtimePathActive) {
      this.realtimePathActive = false;
      this.realtimeRelay().stop();
      this.realtimeSession().end("closed").subscribe();
    }
    // Before closeVoiceAudio(), which closes the context the Realtime analysers hang off.
    this.stopRealtimeVoiceUi();
    this.closeVoiceAudio();
    this.voiceState = "off";
    this.stopDictation();
    this.inputText = "";
    this.onInputResize();
    // The thread has been building underneath the whole time and is about to be revealed. Land
    // the reader at the newest message rather than wherever they were before voice mode started.
    if (wasActive) this.scheduleScrollToBottom("auto");
  }
}
